// src/components/Navbar.js
import React, { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import { Link, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import styled from 'styled-components';
import useMediaQuery from '@mui/material/useMediaQuery';
import ClickAwayListener from '@mui/material/ClickAwayListener';

import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider as MuiDivider,
  Stack,
  Typography,
  Breadcrumbs,
  Link as MuiLink,
  Fab,
} from '@mui/material';

import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CategoryIcon from '@mui/icons-material/Category';
import GroupIcon from '@mui/icons-material/Group';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import PeopleIcon from '@mui/icons-material/People';
import CalculateIcon from '@mui/icons-material/Calculate';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import BusinessIcon from '@mui/icons-material/Business';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ApartmentIcon from '@mui/icons-material/Apartment';
import DifferenceIcon from '@mui/icons-material/Difference';

import WifiIcon from '@mui/icons-material/Wifi';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import WifiOffIcon from '@mui/icons-material/WifiOff';

import CloudSyncIcon from '@mui/icons-material/CloudSync';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import InventoryIcon from '@mui/icons-material/Inventory';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import AddBoxIcon from '@mui/icons-material/AddBox';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import PublicIcon from '@mui/icons-material/Public';
import PunchClockIcon from '@mui/icons-material/PunchClock';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import PaymentsIcon from '@mui/icons-material/Payments';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SettingsIcon from '@mui/icons-material/Settings';
import OutboundIcon from '@mui/icons-material/Outbound';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import HandymanIcon from '@mui/icons-material/Handyman';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import CarRepairIcon from '@mui/icons-material/CarRepair';
import AppsIcon from '@mui/icons-material/Apps';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CarCrashIcon from '@mui/icons-material/CarCrash';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';

import api from '../../api/api';
import logo from '../assets/logo.png';
import logo2 from '../assets/logo2.png';

/* ────────────────────────────── AUTH EVENTS ────────────────────────────── */

const AUTH_EVENT_KEY = 'integranube_auth_event';

/* ────────────────────────────── NET STATUS ────────────────────────────── */

const NET_OK = 'ok';
const NET_SLOW = 'slow';
const NET_DOWN = 'down';

const colorMap = {
  [NET_OK]: '#2ecc71',
  [NET_SLOW]: '#f1c40f',
  [NET_DOWN]: '#e74c3c',
};

const labelMap = {
  [NET_OK]: 'Online',
  [NET_SLOW]: 'Revisar',
  [NET_DOWN]: 'Error',
};

/* ────────────────────────────── MENÚ USUARIO ────────────────────────────── */

const userMenuItems = [
  {
    label: 'Mi Perfil',
    path: '/mi-perfil',
    permissions: ['usuario:mi-perfil:ver'],
    icon: <AccountCircleIcon color="primary" fontSize="small" />,
  },
  {
    label: 'Cambiar empresa',
    path: null,
    public: true,
    icon: <BusinessIcon color="success" fontSize="small" />,
  },
  { divider: true },
  {
    label: 'Cerrar sesión',
    path: null,
    public: true,
    icon: <ExitToAppIcon color="error" fontSize="small" />,
  },
];

/* ────────────────────────────── MENÚ PRINCIPAL ────────────────────────────── */

const menuItems = [
  {
    label: 'ADQUISICIONES',
    icon: <ShoppingCartIcon fontSize="small" />,
    items: [
      {
        label: 'Facturas de Compra',
        path: '/compras',
        permissions: ['adquisiciones:compras:ver'],
        icon: <FormatListBulletedIcon color="primary" fontSize="small" />,
      },
      {
        label: 'Compras históricas',
        path: '/compras/historicas',
        permissions: ['adquisiciones:compras:ver'],
        icon: <ReceiptLongIcon color="info" fontSize="small" />,
      },
      {
        label: 'Proveedores',
        path: '/proveedores',
        permissions: ['adquisiciones:proveedores:ver'],
        icon: <GroupIcon color="secondary" fontSize="small" />,
      },
      {
        label: 'Carga Masiva Proveedores',
        path: '/proveedores/carga-masiva',
        permissions: ['adquisiciones:proveedores:carga-masiva'],
        icon: <CloudUploadIcon color="info" fontSize="small" />,
      },
      { divider: true },
      {
        label: 'Notas de Crédito (Compras)',
        path: '/adquisiciones/notas-credito',
        permissions: ['adquisiciones:notas-credito:ver'],
        icon: <ReceiptLongIcon color="warning" fontSize="small" />,
      },
    ],
  },
  {
    label: 'APPS',
    icon: <AppsIcon fontSize="small" />,
    items: [
      {
        label: 'Portal de aplicaciones',
        path: '/apps',
        permissions: ['apps:portal:ver'],
        icon: <AppsIcon color="primary" fontSize="small" />,
      },
      {
        label: 'Inventario rápido',
        path: '/apps/inventario',
        permissions: ['apps:inventario-rapido:ver'],
        icon: <InventoryIcon color="success" fontSize="small" />,
      },
    ],
  },
  {
    label: 'AUTOMOTRIZ',
    icon: <DirectionsCarIcon fontSize="small" />,
    items: [
      {
        label: 'Vehículos',
        path: '/automotriz/vehiculos',
        permissions: ['automotriz:vehiculos:ver'],
        icon: <DirectionsCarIcon color="primary" fontSize="small" />,
      },
      { divider: true },
      {
        label: 'Mantenciones',
        path: '/automotriz/mantenciones',
        permissions: ['automotriz:mantenciones:ver'],
        icon: <CarRepairIcon color="warning" fontSize="small" />,
      },
      { divider: true },
      {
        label: 'Catálogo Automotriz',
        path: '/automotriz/catalogo',
        permissions: ['automotriz:catalogo:ver'],
        icon: <CarCrashIcon color="warning" fontSize="small" />,
      },
    ],
  },
  {
    label: 'CONFIGURACIÓN',
    icon: <SettingsIcon fontSize="small" />,
    items: [
      {
        label: 'Mi Empresa',
        path: '/configuraciones/empresa',
        permissions: ['configuraciones:empresa:ver'],
        icon: <ApartmentIcon color="primary" fontSize="small" />,
      },
      {
        label: 'Configuraciones Generales',
        path: '/configuraciones/generales',
        permissions: ['configuraciones:generales:ver'],
        icon: <DisplaySettingsIcon color="info" fontSize="small" />,
      },
    ],
  },
  {
    label: 'CONTABILIDAD',
    icon: <CalculateIcon fontSize="small" />,
    items: [
      {
        label: 'Dashboard Contable',
        path: '/contabilidad/dashboard',
        permissions: ['contabilidad:dashboard:ver'],
        icon: <DashboardIcon color="primary" fontSize="small" />,
      },
      {
        label: 'Plan de Cuentas',
        path: '/contabilidad/plan-de-cuentas',
        permissions: ['contabilidad:plan-cuentas:ver'],
        icon: <AccountBalanceWalletIcon color="secondary" fontSize="small" />,
      },
      {
        label: 'Registros Contables',
        path: '/contabilidad/registros-contables',
        permissions: ['contabilidad:registros:ver'],
        icon: <ReceiptLongIcon color="info" fontSize="small" />,
      },
      {
        label: 'Balances',
        path: '/contabilidad/balances',
        permissions: ['contabilidad:balances:ver'],
        icon: <DifferenceIcon color="warning" fontSize="small" />,
      },
      {
        label: 'Gastos',
        path: '/contabilidad/gastos',
        permissions: ['contabilidad:gastos:ver'],
        icon: <ReceiptLongIcon color="error" fontSize="small" />,
      },
      {
        label: 'Pagos masivos',
        path: '/contabilidad/pagos-masivos',
        permissions: ['contabilidad:pagos-masivos:ver'],
        icon: <PaymentsIcon color="error" fontSize="small" />,
      },
    ],
  },
  {
    label: 'CRM',
    icon: <PeopleIcon fontSize="small" />,
    items: [
      {
        label: 'Dashboard CRM',
        path: '/crm',
        permissions: ['crm:dashboard:ver'],
        icon: <DashboardIcon color="primary" fontSize="small" />,
      },
      { divider: true },
      {
        label: 'Contactos',
        icon: <PeopleIcon color="primary" fontSize="small" />,
        items: [
          {
            label: 'Listado de contactos',
            path: '/crm/contactos',
            permissions: ['crm:contactos:ver'],
            icon: <FormatListBulletedIcon color="primary" fontSize="small" />,
          },
          {
            label: 'Crear contacto',
            path: '/crm/contactos/crear',
            permissions: ['crm:contactos:crear'],
            icon: <AddBoxIcon color="success" fontSize="small" />,
          },
          {
            label: 'Carga masiva contactos',
            path: '/crm/contactos/carga-masiva',
            permissions: ['crm:contactos:carga-masiva'],
            icon: <CloudUploadIcon color="info" fontSize="small" />,
          },
        ],
      },
      {
        label: 'Empresas',
        icon: <BusinessIcon color="secondary" fontSize="small" />,
        items: [
          {
            label: 'Listado de empresas',
            path: '/crm/empresas',
            permissions: ['crm:empresas:ver'],
            icon: <FormatListBulletedIcon color="primary" fontSize="small" />,
          },
          {
            label: 'Crear empresa',
            path: '/crm/empresas/crear',
            permissions: ['crm:empresas:crear'],
            icon: <AddBoxIcon color="success" fontSize="small" />,
          },
          {
            label: 'Carga masiva empresas',
            path: '/crm/empresas/carga-masiva',
            permissions: ['crm:empresas:carga-masiva'],
            icon: <CloudUploadIcon color="info" fontSize="small" />,
          },
        ],
      },
      { divider: true },
      {
        label: 'Oportunidades',
        icon: <RequestQuoteIcon color="success" fontSize="small" />,
        items: [
          {
            label: 'Pipeline comercial',
            path: '/crm/oportunidades',
            permissions: ['crm:oportunidades:ver'],
            icon: <RequestQuoteIcon color="success" fontSize="small" />,
          },
          {
            label: 'Crear oportunidad',
            path: '/crm/oportunidades/crear',
            permissions: ['crm:oportunidades:crear'],
            icon: <AddBoxIcon color="success" fontSize="small" />,
          },
        ],
      },
      {
        label: 'Casos',
        icon: <AssignmentTurnedInIcon color="warning" fontSize="small" />,
        items: [
          {
            label: 'Listado de casos',
            path: '/crm/casos',
            permissions: ['crm:casos:ver'],
            icon: <AssignmentTurnedInIcon color="warning" fontSize="small" />,
          },
          {
            label: 'Crear caso',
            path: '/crm/casos/crear',
            permissions: ['crm:casos:crear'],
            icon: <AddBoxIcon color="success" fontSize="small" />,
          },
        ],
      },
      {
        label: 'Actividades',
        icon: <ScheduleIcon color="info" fontSize="small" />,
        items: [
          {
            label: 'Agenda de actividades',
            path: '/crm/actividades',
            permissions: ['crm:actividades:ver'],
            icon: <ScheduleIcon color="info" fontSize="small" />,
          },
          {
            label: 'Crear actividad',
            path: '/crm/actividades/crear',
            permissions: ['crm:actividades:crear'],
            icon: <AddBoxIcon color="success" fontSize="small" />,
          },
        ],
      },
      { divider: true },
      {
        label: 'Campañas',
        icon: <CloudSyncIcon color="secondary" fontSize="small" />,
        items: [
          {
            label: 'Listado de campañas',
            path: '/crm/campanas',
            permissions: ['crm:campanas:ver'],
            icon: <CloudSyncIcon color="secondary" fontSize="small" />,
          },
          {
            label: 'Crear campaña',
            path: '/crm/campanas/crear',
            permissions: ['crm:campanas:crear'],
            icon: <AddBoxIcon color="success" fontSize="small" />,
          },
        ],
      },
      {
        label: 'Seguimientos',
        path: '/crm/seguimientos',
        permissions: ['crm:seguimientos:ver'],
        icon: <MoreTimeIcon color="success" fontSize="small" />,
      },
      {
        label: 'Catálogos CRM',
        path: '/crm/catalogos',
        permissions: ['crm:catalogos:ver'],
        icon: <CategoryIcon color="warning" fontSize="small" />,
      },
      {
        label: 'Reportes CRM',
        path: '/crm/reportes',
        permissions: ['crm:reportes:ver'],
        icon: <AssessmentIcon color="primary" fontSize="small" />,
      },
      {
        label: 'Configuración CRM',
        path: '/crm/configuracion',
        permissions: ['crm:configuracion:ver'],
        icon: <SettingsIcon color="warning" fontSize="small" />,
      },
    ],
  },
  {
    label: 'FINANZAS',
    icon: <AccountBalanceWalletIcon fontSize="small" />,
    items: [
      {
        label: 'Resumen financiero',
        path: '/finanzas/resumen',
        permissions: ['finanzas:movimientos:ver'],
        icon: <DashboardIcon color="primary" fontSize="small" />,
      },
      {
        label: 'Libro de movimientos',
        path: '/finanzas/movimientos',
        permissions: ['finanzas:movimientos:ver'],
        icon: <SyncAltIcon color="info" fontSize="small" />,
      },
      {
        label: 'Operaciones financieras',
        path: '/finanzas/operaciones',
        permissions: ['finanzas:operaciones:ver'],
        icon: <ReceiptLongIcon color="primary" fontSize="small" />,
      },
      {
        label: 'Liquidaciones',
        path: '/finanzas/liquidaciones',
        permissions: ['finanzas:liquidaciones:ver'],
        icon: <PriceCheckIcon color="success" fontSize="small" />,
      },
      { divider: true },
      {
        label: 'Configuración financiera',
        path: '/finanzas/configuracion',
        permissions: ['finanzas:cuentas:ver', 'finanzas:metodos:ver', 'finanzas:condiciones:ver'],
        icon: <SettingsIcon color="primary" fontSize="small" />,
      },
      { divider: true },
      {
        label: 'Cuentas financieras',
        path: '/finanzas/cuentas',
        permissions: ['finanzas:cuentas:ver'],
        icon: <AccountBalanceIcon color="secondary" fontSize="small" />,
      },
      {
        label: 'Métodos de pago',
        path: '/finanzas/metodos-pago',
        permissions: ['finanzas:metodos:ver'],
        icon: <PaymentsIcon color="info" fontSize="small" />,
      },
      {
        label: 'Condiciones de pago',
        path: '/finanzas/condiciones-pago',
        permissions: ['finanzas:condiciones:ver'],
        icon: <MoreTimeIcon color="warning" fontSize="small" />,
      },
      {
        label: 'Destinos de pago',
        path: '/finanzas/metodos-cuentas',
        permissions: ['finanzas:metodos:ver'],
        icon: <SwapHorizIcon color="success" fontSize="small" />,
      },
    ],
  },
  {
    label: 'INTEGRACIONES',
    icon: <CloudSyncIcon fontSize="small" />,
    items: [
      {
        label: 'Jumpseller – Configuración',
        path: '/integraciones/jumpseller',
        permissions: ['integraciones:jumpseller:ver'],
        icon: <ShoppingCartIcon color="info" fontSize="small" />,
      },
      {
        label: 'MercadoLibre/Conf.',
        path: '/integraciones/mercadolibre',
        permissions: ['integraciones:mercadolibre:ver'],
        icon: <ShoppingCartIcon color="warning" fontSize="small" />,
      },
      {
        label: 'MercadoLibre/Gestor',
        path: '/gestor/mercadolibre',
        permissions: ['integraciones:mercadolibre:gestor:ver'],
        icon: <CloudSyncIcon color="secondary" fontSize="small" />,
      },
    ],
  },
  {
    label: 'INVENTARIO',
    icon: <InventoryIcon fontSize="small" />,
    items: [
      {
        label: 'Productos',
        icon: <FormatListBulletedIcon color="primary" fontSize="small" />,
        items: [
          {
            label: 'Listado de Productos',
            path: '/inventario',
            permissions: ['inventario:productos:ver'],
            icon: <FormatListBulletedIcon color="primary" fontSize="small" />,
          },
          {
            label: 'Crear Producto',
            path: '/inventario/crear',
            permissions: ['inventario:productos:crear'],
            icon: <AddBoxIcon color="success" fontSize="small" />,
          },
          {
            label: 'Carga Masiva Productos',
            path: '/inventario/carga-masiva',
            permissions: ['inventario:productos:carga-masiva'],
            icon: <CloudUploadIcon color="info" fontSize="small" />,
          },
        ],
      },
      {
        label: 'Presentaciones',
        path: '/inventario/unidades',
        permissions: ['inventario:unidades:ver'],
        icon: <CategoryIcon color="warning" fontSize="small" />,
      },
      { divider: true },
      {
        label: 'Categorías Productos',
        path: '/inventario/categorias',
        permissions: ['inventario:categorias:ver'],
        icon: <AccountTreeIcon color="secondary" fontSize="small" />,
      },
      {
        label: 'Marcas Productos',
        path: '/inventario/marcas',
        permissions: ['inventario:marcas:ver'],
        icon: <BrandingWatermarkIcon color="primary" fontSize="small" />,
      },
      {
        label: 'Orígenes Productos',
        path: '/inventario/origenes',
        permissions: ['inventario:origenes:ver'],
        icon: <PublicIcon color="primary" fontSize="small" />,
      },
      { divider: true },
      {
        label: 'Bodegas',
        path: '/inventario/bodegas',
        permissions: ['inventario:bodegas:ver'],
        icon: <WarehouseIcon color="error" fontSize="small" />,
      },
      {
        label: 'Realizar Inventario',
        path: '/inventario/tomas',
        permissions: ['inventario:tomas:ver'],
        icon: <AssignmentTurnedInIcon color="success" fontSize="small" />,
      },
      {
        label: 'Traslados',
        path: '/inventario/traslados',
        permissions: ['inventario:traslados:ver'],
        icon: <SwapHorizIcon color="warning" fontSize="small" />,
      },
      {
        label: 'Movimientos manuales',
        path: '/inventario/movimientos-manuales',
        permissions: ['inventario:movimientos-manuales:ver'],
        icon: <SyncAltIcon color="info" fontSize="small" />,
      },
      {
        label: 'Consumos internos',
        path: '/inventario/consumos',
        permissions: ['inventario:consumos:ver'],
        icon: <RemoveCircleOutlineIcon color="error" fontSize="small" />,
      },
      { divider: true },
      {
        label: 'Kardex de movimientos',
        path: '/inventario/kardex',
        permissions: ['inventario:kardex:ver'],
        icon: <ReceiptLongIcon color="primary" fontSize="small" />,
      },
      {
        label: 'Informes',
        path: '/inventario/informes',
        permissions: ['inventario:informes:ver'],
        icon: <AssessmentIcon color="primary" fontSize="small" />,
      },
    ],
  },
  {
    label: 'LOGISTICA',
    icon: <LocalShippingIcon fontSize="small" />,
    items: [
      {
        label: 'Administración',
        path: '/logistica/administracion',
        permissions: ['logistica:administracion:ver'],
        icon: <SettingsIcon color="primary" fontSize="small" />,
      },
      {
        label: 'Gestión de despachos',
        path: '/logistica/despachos',
        permissions: ['logistica:despachos:ver'],
        icon: <OutboundIcon color="primary" fontSize="small" />,
      },
    ],
  },
  {
    label: 'RRHH',
    icon: <PeopleIcon fontSize="small" />,
    items: [
      {
        label: 'Mi Portal de RRHH',
        path: '/rrhh/portal',
        permissions: ['rrhh:portal:ver-propia'],
        icon: <AccountCircleIcon color="success" fontSize="small" />,
      },
      {
        label: 'Trabajadores',
        path: '/rrhh/trabajadores',
        permissions: ['rrhh:trabajadores:ver'],
        icon: <PeopleIcon color="primary" fontSize="small" />,
      },
      {
        label: 'Contratos',
        path: '/rrhh/contratos',
        permissions: ['rrhh:contratos:ver'],
        icon: <ReceiptLongIcon color="primary" fontSize="small" />,
      },
      { divider: true },
      {
        label: 'Asistencia',
        path: '/rrhh/asistencia',
        permissions: ['rrhh:asistencia:ver-propia', 'rrhh:asistencia-resumen:ver'],
        icon: <MoreTimeIcon color="success" fontSize="small" />,
      },
      {
        label: 'Resumen de asistencia',
        path: '/rrhh/asistencia/resumen',
        permissions: ['rrhh:asistencia-resumen:ver'],
        icon: <FactCheckIcon color="primary" fontSize="small" />,
      },
      {
        label: 'Marcaciones',
        path: '/rrhh/asistencia/marcaciones',
        permissions: ['rrhh:marcaciones:ver'],
        icon: <PunchClockIcon color="info" fontSize="small" />,
      },
      {
        label: 'Asistencia y novedades',
        path: '/rrhh/asistencia-novedades',
        permissions: ['rrhh:marcaciones:ver'],
        icon: <FactCheckIcon color="warning" fontSize="small" />,
      },
      {
        label: 'Calendario',
        path: '/rrhh/asistencia/calendario',
        permissions: ['rrhh:calendario:ver'],
        icon: <CalendarMonthIcon color="info" fontSize="small" />,
      },
      {
        label: 'Ausencias',
        path: '/rrhh/ausencias',
        permissions: ['rrhh:ausencias:ver'],
        icon: <EventBusyIcon color="error" fontSize="small" />,
      },
      {
        label: 'Turnos',
        path: '/rrhh/turnos',
        permissions: ['rrhh:turnos:ver'],
        icon: <ScheduleIcon color="secondary" fontSize="small" />,
      },
      {
        label: 'Asignaciones a Turnos',
        path: '/rrhh/turnos/asignacion',
        permissions: ['rrhh:turnos:asignar'],
        icon: <SyncAltIcon color="secondary" fontSize="small" />,
      },
      { divider: true },
      {
        label: 'Liquidaciones',
        path: '/rrhh/liquidaciones',
        permissions: ['rrhh:liquidaciones:ver'],
        icon: <RequestQuoteIcon color="success" fontSize="small" />,
      },
      { divider: true },
      {
        label: 'Configuración de remuneraciones',
        icon: <SettingsIcon color="warning" fontSize="small" />,
        items: [
          {
            label: 'Conceptos de remuneración',
            path: '/rrhh/conceptos-remuneracion',
            permissions: ['rrhh:catalogos:ver'],
            icon: <ReceiptLongIcon color="primary" fontSize="small" />,
          },
          {
            label: 'Parámetros de remuneraciones',
            path: '/rrhh/parametros-remuneraciones',
            permissions: ['rrhh:catalogos:ver'],
            icon: <SettingsIcon color="info" fontSize="small" />,
          },
          {
          label: 'Catálogos',
          path: '/rrhh/catalogos',
          permissions: ['rrhh:catalogos:ver'],
          icon: <ReceiptLongIcon color="action" fontSize="small" />,
        },
        ],
      },
    ],
  },
  {
    label: 'REPORTES',
    icon: <AssessmentIcon fontSize="small" />,
    items: [
      {
        label: 'Reportes operacionales',
        path: '/reportes',
        permissions: ['utilidades:reportes:ver'],
        icon: <AssessmentIcon color="primary" fontSize="small" />,
      },
      { divider: true },
      {
        label: 'Rentabilidad de ventas',
        path: '/reportes/rentabilidad-ventas',
        permissions: ['reportes:reportes:ver'],
        keywords: ['ventas', 'costo', 'fifo', 'margen', 'utilidad', 'periodo'],
        icon: <TrendingUpIcon color="success" fontSize="small" />,
      },
      {
        label: 'Rentabilidad por producto',
        path: '/reportes/rentabilidad-productos',
        permissions: ['reportes:reportes:ver'],
        keywords: ['productos', 'sku', 'ranking', 'mayor venta', 'margen', 'utilidad', 'fifo'],
        icon: <InventoryIcon color="info" fontSize="small" />,
      },
    ],
  },
  {
    label: 'SALUD',
    icon: <LocalHospitalIcon fontSize="small" />,
    items: [
      {
        label: 'Dashboard Salud',
        path: '/salud',
        permissions: ['salud:resumen:ver'],
        icon: <DashboardIcon color="primary" fontSize="small" />,
      },
      { divider: true },
      {
        label: 'Pacientes',
        path: '/salud/pacientes',
        permissions: ['salud:pacientes:ver'],
        icon: <PeopleIcon color="primary" fontSize="small" />,
      },
      {
        label: 'Cirugías',
        path: '/salud/cirugias',
        permissions: ['salud:cirugias:ver'],
        icon: <AssignmentTurnedInIcon color="info" fontSize="small" />,
      },
      {
        label: 'Agenda',
        path: '/salud/agenda',
        permissions: ['salud:agenda:ver'],
        icon: <CalendarMonthIcon color="info" fontSize="small" />,
      },
      {
        label: 'Profesionales',
        path: '/salud/profesionales',
        permissions: ['salud:profesionales:ver'],
        icon: <AccountCircleIcon color="success" fontSize="small" />,
      },
      {
        label: 'Prestaciones',
        path: '/salud/prestaciones',
        permissions: ['salud:prestaciones:ver'],
        icon: <CategoryIcon color="warning" fontSize="small" />,
      },
    ],
  },
  {
    label: 'TESORERIA',
    icon: <AccountBalanceWalletIcon fontSize="small" />,
    items: [
      {
        label: 'Dashboard Tesorería',
        path: '/tesoreria',
        permissions: ['tesoreria:dashboard:ver'],
        icon: <DashboardIcon color="primary" fontSize="small" />,
      },
      {
        label: 'Cuentas de Tesorería',
        path: '/tesoreria/cuentas',
        permissions: ['tesoreria:cuentas:ver'],
        icon: <AccountBalanceIcon color="secondary" fontSize="small" />,
      },
      {
        label: 'Pagos',
        path: '/tesoreria/pagos',
        permissions: ['tesoreria:pagos:ver'],
        icon: <PaymentsIcon color="primary" fontSize="small" />,
      },
      {
        label: 'Movimientos',
        path: '/tesoreria/movimientos',
        permissions: ['tesoreria:movimientos:ver'],
        icon: <SyncAltIcon color="info" fontSize="small" />,
      },
      {
        label: 'Transferencias',
        path: '/tesoreria/transferencias',
        permissions: ['tesoreria:transferencias:ver'],
        icon: <SwapHorizIcon color="secondary" fontSize="small" />,
      },
      {
        label: 'Conciliación bancaria',
        path: '/tesoreria/conciliacion-bancaria',
        permissions: ['tesoreria:conciliacion-bancaria:ver'],
        icon: <FactCheckIcon color="success" fontSize="small" />,
      },
      {
        label: 'Liquidaciones',
        path: '/tesoreria/liquidaciones',
        permissions: ['tesoreria:liquidaciones:ver'],
        icon: <PriceCheckIcon color="success" fontSize="small" />,
      },
      { divider: true },
      {
        label: 'Configuración de pagos',
        path: '/tesoreria/configuracion-pagos',
        permissions: ['tesoreria:configuracion-pagos:ver'],
        icon: <SettingsIcon color="warning" fontSize="small" />,
      },
      {
        label: 'Métodos y Contabilidad',
        path: '/tesoreria/metodos-contabilidad',
        permissions: ['tesoreria:metodos-contabilidad:ver'],
        icon: <ReceiptLongIcon color="success" fontSize="small" />,
      },
    ],
  },
  {
    label: 'UTILIDADES',
    icon: <HandymanIcon fontSize="small" />,
    items: [
      {
        label: 'Reportes',
        path: '/reportes',
        permissions: ['utilidades:reportes:ver'],
        icon: <AssessmentIcon color="primary" fontSize="small" />,
      },
      {
        label: 'Herramientas',
        path: '/herramientas',
        permissions: ['herramientas:general:ver'],
        icon: <HandymanIcon color="error" fontSize="small" />,
      },
    ],
  },
  {
    label: 'VENTAS',
    icon: <PointOfSaleIcon fontSize="small" />,
    items: [
      {
        label: 'POS/Punto de Venta',
        path: '/pos',
        permissions: ['pos:terminal:ver'],
        icon: <PointOfSaleIcon color="primary" fontSize="small" />,
      },
      {
        label: 'Gestor de cajas',
        path: '/gestor/cajas',
        permissions: ['caja:gestion:ver'],
        icon: <PointOfSaleIcon color="info" fontSize="small" />,
      },
      {
        label: 'Reglas del POS',
        path: '/ventas/reglas',
        permissions: ['ventas:reglas:ver'],
        icon: <LocalAtmIcon color="secondary" fontSize="small" />,
      },
      { divider: true },
      {
        label: 'Resumen de ventas',
        path: '/ventas',
        permissions: ['ventas:ventas:ver'],
        icon: <ReceiptLongIcon color="secondary" fontSize="small" />,
      },
      {
        label: 'Pagos de ventas',
        path: '/ventas/pagos',
        permissions: ['ventas:pagos:ver'],
        icon: <PaymentsIcon color="primary" fontSize="small" />,
      },
      {
        label: 'Facturación',
        icon: <FormatListBulletedIcon color="primary" fontSize="small" />,
        items: [
          {
            label: 'Resumen facturación',
            path: '/facturacion',
            permissions: ['facturacion:resumen:ver'],
            icon: <DashboardIcon color="primary" fontSize="small" />,
          },
          {
            label: 'Facturas Afectas (33)',
            path: '/facturacion/facturas-afectas',
            permissions: ['facturacion:facturas-afectas:ver'],
            icon: <ReceiptLongIcon color="error" fontSize="small" />,
          },
          {
            label: 'Facturas Exentas (34)',
            path: '/facturacion/facturas-exentas',
            permissions: ['facturacion:facturas-exentas:ver'],
            icon: <ReceiptLongIcon color="warning" fontSize="small" />,
          },
          {
            label: 'Boletas (39/41)',
            path: '/facturacion/boletas',
            permissions: ['facturacion:boletas:ver'],
            icon: <ReceiptLongIcon color="info" fontSize="small" />,
          },
          {
            label: 'Notas de Débito (56)',
            path: '/facturacion/notas-debito',
            permissions: ['facturacion:notas-debito:ver'],
            icon: <ReceiptLongIcon color="warning" fontSize="small" />,
          },
          {
            label: 'Notas de Crédito (61)',
            path: '/facturacion/notas-credito',
            permissions: ['facturacion:notas-credito:ver'],
            icon: <ReceiptLongIcon color="secondary" fontSize="small" />,
          },
          {
            label: 'Timbraje/Folios',
            path: '/facturacion/timbraje-folios',
            permissions: ['facturacion:folios:ver'],
            icon: <ReceiptLongIcon color="primary" fontSize="small" />,
          },
          {
            label: 'Libro de compras',
            path: '/facturacion/libro-compras',
            permissions: ['facturacion:libro-compras:ver'],
            icon: <ReceiptLongIcon color="success" fontSize="small" />,
          },
          {
            label: 'Libro de ventas',
            path: '/facturacion/libro-ventas',
            permissions: ['facturacion:libro-ventas:ver'],
            icon: <ReceiptLongIcon color="success" fontSize="small" />,
          },
          {
            label: 'Certificados SII',
            path: '/facturacion/certificados-sii',
            permissions: ['facturacion:certificados-sii:ver'],
            icon: <ReceiptLongIcon color="info" fontSize="small" />,
          },
          {
            label: 'Configuración SII',
            path: '/facturacion/configuracion',
            permissions: ['facturacion:configuracion:ver'],
            icon: <SettingsIcon color="warning" fontSize="small" />,
          },
        ],
      },
      {
        label: 'DTEs Administrativos',
        icon: <ReceiptLongIcon color="info" fontSize="small" />,
        items: [
          {
            label: 'Emitir Nota de Débito (56)',
            path: '/facturacion/notas-debito/emitir',
            permissions: ['facturacion:notas-debito:emitir'],
            icon: <ReceiptLongIcon color="warning" fontSize="small" />,
          },
          {
            label: 'Emitir Nota de Crédito (61)',
            path: '/facturacion/notas-credito/emitir',
            permissions: ['facturacion:notas-credito:emitir'],
            icon: <ReceiptLongIcon color="secondary" fontSize="small" />,
          },
        ],
      },
      {
        label: 'POS Administrativo',
        path: '/pos/administrativo',
        permissions: ['pos:terminal:ver'],
        icon: <PointOfSaleIcon color="info" fontSize="small" />,
      },
      {
        label: 'Pedidos',
        path: '/ventas/pedidos',
        permissions: ['ventas:pedidos:ver'],
        icon: <ShoppingCartIcon color="warning" fontSize="small" />,
      },
      {
        label: 'Cotizaciones',
        path: '/ventas/cotizaciones',
        permissions: ['ventas:cotizaciones:ver'],
        icon: <RequestQuoteIcon color="secondary" fontSize="small" />,
      },
      {
        label: 'Clientes',
        path: '/ventas/clientes',
        permissions: ['ventas:clientes:ver'],
        icon: <PeopleIcon color="error" fontSize="small" />,
      },
    ],
  },
  {
    label: 'WEBSTORE',
    icon: <SettingsIcon fontSize="small" />,
    items: [
      {
        label: 'Administración',
        path: '/webstore/administracion',
        permissions: ['webstore:administracion:ver'],
        icon: <SettingsIcon color="primary" fontSize="small" />,
      },
    ],
  },
];

/* ────────────────────────────── BREADCRUMBS ────────────────────────────── */

const friendlyMap = {
  '/': 'Inicio',
  '/apps': 'Apps',
  '/apps/inventario': 'Inventario rápido',
  '/apps/inventario-rapido': 'Inventario rápido',

  '/crm': 'CRM',
  '/crm/dashboard': 'Dashboard CRM',
  '/crm/contactos': 'Contactos',
  '/crm/contactos/crear': 'Crear contacto',
  '/crm/contactos/carga-masiva': 'Carga masiva de contactos',
  '/crm/empresas': 'Empresas',
  '/crm/empresas/crear': 'Crear empresa',
  '/crm/empresas/carga-masiva': 'Carga masiva de empresas',
  '/crm/oportunidades': 'Oportunidades',
  '/crm/oportunidades/crear': 'Crear oportunidad',
  '/crm/casos': 'Casos',
  '/crm/casos/crear': 'Crear caso',
  '/crm/actividades': 'Actividades',
  '/crm/actividades/crear': 'Crear actividad',
  '/crm/campanas': 'Campañas',
  '/crm/campanas/crear': 'Crear campaña',
  '/crm/seguimientos': 'Seguimientos',
  '/crm/catalogos': 'Catálogos CRM',
  '/crm/reportes': 'Reportes CRM',
  '/crm/configuracion': 'Configuración CRM',

  '/compras': 'Compras',
  '/compras/historicas': 'Compras históricas',
  '/proveedores': 'Proveedores',
  '/proveedores/carga-masiva': 'Carga masiva de proveedores',
  '/adquisiciones/notas-credito': 'Notas de crédito (Compras)',

  '/contabilidad': 'Contabilidad',
  '/contabilidad/dashboard': 'Dashboard contable',
  '/contabilidad/plan-de-cuentas': 'Plan de cuentas',
  '/contabilidad/registros-contables': 'Registros contables',
  '/contabilidad/balances': 'Balances',
  '/contabilidad/gastos': 'Gastos',
  '/contabilidad/pagos-masivos': 'Pagos masivos',

  '/finanzas': 'Finanzas',
  '/finanzas/resumen': 'Resumen financiero',
  '/finanzas/movimientos': 'Libro de movimientos',
  '/finanzas/operaciones': 'Operaciones financieras',
  '/finanzas/liquidaciones': 'Liquidaciones',
  '/finanzas/configuracion': 'Configuración financiera',
  '/finanzas/cuentas': 'Cuentas financieras',
  '/finanzas/metodos-pago': 'Métodos de pago',
  '/finanzas/condiciones-pago': 'Condiciones de pago',
  '/finanzas/metodos-cuentas': 'Destinos de pago',

  '/inventario': 'Inventario',
  '/inventario/crear': 'Crear producto',
  '/inventario/carga-masiva': 'Carga masiva de productos',
  '/inventario/unidades': 'Presentaciones',
  '/inventario/categorias': 'Categorías productos',
  '/inventario/marcas': 'Marcas productos',
  '/inventario/origenes': 'Orígenes productos',
  '/inventario/bodegas': 'Bodegas',
  '/inventario/tomas': 'Realizar inventario',
  '/inventario/traslados': 'Traslados',
  '/inventario/movimientos-manuales': 'Movimientos manuales',
  '/inventario/consumos': 'Consumos internos',
  '/inventario/kardex': 'Kardex de movimientos',
  '/inventario/informes': 'Informes',

  '/pos': 'Punto de venta',
  '/pos/administrativo': 'POS Administrativo',
  '/POS': 'Punto de venta',
  '/ventas': 'Ventas',
  '/ventas/pagos': 'Pagos de ventas',
  '/gestor/cajas': 'Gestor de cajas',
  '/ventas/pedidos': 'Pedidos',
  '/ventas/cotizaciones': 'Cotizaciones',
  '/ventas/clientes': 'Clientes',
  '/ventas/reglas': 'Reglas del POS',

  '/facturacion': 'Facturación',
  '/facturacion/facturas-afectas': 'Facturas Afectas (33)',
  '/facturacion/facturas-exentas': 'Facturas Exentas (34)',
  '/facturacion/boletas': 'Boletas (39/41)',
  '/facturacion/notas-debito': 'Notas de Débito (56)',
  '/facturacion/notas-credito': 'Notas de Crédito (61)',
  '/facturacion/notas-debito/emitir': 'Emitir nota de débito',
  '/facturacion/notas-credito/emitir': 'Emitir nota de crédito',
  '/facturacion/timbraje-folios': 'Timbraje/Folios',
  '/facturacion/libro-compras': 'Libro de compras',
  '/facturacion/libro-ventas': 'Libro de ventas',
  '/facturacion/certificados-sii': 'Certificados SII',
  '/facturacion/configuracion': 'Configuración SII',

  '/integraciones': 'Integraciones',
  '/integraciones/jumpseller': 'Jumpseller – Configuración',
  '/integraciones/mercadolibre': 'MercadoLibre – Configuración',
  '/gestor/mercadolibre': 'MercadoLibre – Gestor',

  '/rrhh': 'RRHH',
  '/rrhh/portal': 'Portal RRHH',
  '/rrhh/trabajadores': 'Trabajadores',
  '/rrhh/contratos': 'Contratos',
  '/rrhh/asistencia': 'Asistencia',
  '/rrhh/asistencia/resumen': 'Resumen de asistencia',
  '/rrhh/asistencia/marcaciones': 'Marcaciones',
  '/rrhh/asistencia-novedades': 'Asistencia y novedades',
  '/rrhh/asistencia/calendario': 'Calendario',
  '/rrhh/ausencias': 'Ausencias',
  '/rrhh/turnos': 'Turnos',
  '/rrhh/turnos/asignacion': 'Asignaciones a Turnos',
  '/rrhh/liquidaciones': 'Liquidaciones',
  '/rrhh/catalogos': 'Catálogos',
  '/rrhh/conceptos-remuneracion': 'Conceptos de remuneración',
  '/rrhh/parametros-remuneraciones': 'Parámetros de remuneraciones',

  '/salud': 'Salud',
  '/salud/pacientes': 'Pacientes',
  '/salud/agenda': 'Agenda',
  '/salud/profesionales': 'Profesionales',
  '/salud/prestaciones': 'Prestaciones',
  '/salud/atenciones': 'Atenciones',

  '/tesoreria': 'Tesorería',
  '/tesoreria/cuentas': 'Cuentas de tesorería',
  '/tesoreria/pagos': 'Pagos',
  '/tesoreria/movimientos': 'Movimientos',
  '/tesoreria/transferencias': 'Transferencias',
  '/tesoreria/conciliacion-bancaria': 'Conciliación bancaria',
  '/tesoreria/configuracion-pagos': 'Configuración de pagos',
  '/tesoreria/liquidaciones': 'Liquidación de pagos pendientes',
  '/tesoreria/metodos-contabilidad': 'Métodos y contabilidad',

  '/logistica': 'Logística',
  '/logistica/administracion': 'Administración',
  '/logistica/despachos': 'Gestión de despachos',

  '/configuraciones/empresa': 'Mi empresa',
  '/configuraciones/generales': 'Configuraciones generales',
  '/mi-perfil': 'Mi perfil',
  '/herramientas': 'Herramientas',
  '/reportes': 'Reportes',
  '/reportes/rentabilidad-ventas': 'Rentabilidad de ventas',
  '/reportes/rentabilidad-productos': 'Rentabilidad por producto',
  '/sin-acceso': 'Sin acceso',
};

/* ────────────────────────────── HELPERS ────────────────────────────── */

function safeJsonParse(value, fallback = null) {
  try {
    if (!value) return fallback;
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function getDisplayName(obj, fallback = '') {
  return obj?.name || obj?.nombre || obj?.nombre_completo || obj?.razon_social || obj?.nombre_fantasia || obj?.email || fallback;
}

function getTenantName(tenant) {
  return tenant?.name || tenant?.nombre || tenant?.nombre_fantasia || tenant?.razon_social || tenant?.tenant_name || tenant?.tenantid || tenant?.tenantId || tenant?.id || 'Sin empresa';
}

function getTenantId(source) {
  return String(source?.tenantid || source?.tenantId || source?.tenant_id || source?.id_tenant || source?.id || source?.uuid || '').trim();
}

function getSelectedTenantId(tenantFromStorage) {
  return getTenantId(tenantFromStorage) || String(localStorage.getItem('tenantid') || '').trim();
}

function resolveCurrentTenant(allowedTenants = [], storedTenant = null) {
  const selectedTenantId = getSelectedTenantId(storedTenant);

  if (selectedTenantId) {
    return allowedTenants.find((item) => getTenantId(item) === selectedTenantId) || null;
  }

  return allowedTenants.length === 1 ? allowedTenants[0] : null;
}

function getPermissionCode(permission) {
  if (typeof permission === 'string') return normalizePermission(permission);
  if (!permission || typeof permission !== 'object') return '';

  return normalizePermission(permission.name || permission.permission || permission.code || permission.clave || permission.permission_name);
}

function normalizePermission(value) {
  return String(value || '').trim().toLowerCase();
}

function normalizePermissionList(value) {
  const list = Array.isArray(value) ? value : [];
  return [...new Set(list.map(getPermissionCode).filter(Boolean))];
}

function toTitle(str) {
  return decodeURIComponent(str).replace(/-/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

function isNumericSegment(seg) {
  return /^[0-9]+$/.test(seg);
}

function normalizePath(p) {
  if (!p) return '';
  if (p === '/') return '/';
  return String(p).replace(/\/+$/, '');
}

function collectPaths(nodes = [], acc = new Set()) {
  for (const node of nodes) {
    if (!node || node.divider) continue;
    if (node.path) acc.add(normalizePath(node.path));
    if (Array.isArray(node.items)) collectPaths(node.items, acc);
  }

  return acc;
}

function getNodePermissions(node) {
  if (!node || node.divider || node.public === true) return [];
  if (Array.isArray(node.permissions) && node.permissions.length > 0) return node.permissions.map(normalizePermission);
  return [];
}

function hasNodePermission(node, permissionSet) {
  const requiredPermissions = getNodePermissions(node);
  if (requiredPermissions.length === 0) return true;
  return requiredPermissions.some((permission) => permissionSet.has(permission));
}

function cleanDividers(nodes = []) {
  const result = [];

  for (const node of nodes) {
    if (node.divider) {
      const previous = result[result.length - 1];

      if (result.length > 0 && !previous?.divider) {
        result.push(node);
      }

      continue;
    }

    result.push(node);
  }

  while (result.length > 0 && result[result.length - 1]?.divider) {
    result.pop();
  }

  return result;
}

function filterMenuByPermissions(nodes = [], permissionSet) {
  const filtered = [];

  for (const node of nodes) {
    if (node.divider) {
      filtered.push(node);
      continue;
    }

    const hasChildren = Array.isArray(node.items) && node.items.length > 0;

    if (hasChildren) {
      const visibleChildren = filterMenuByPermissions(node.items, permissionSet);

      if (visibleChildren.length > 0) {
        filtered.push({ ...node, items: visibleChildren });
      }

      continue;
    }

    if (hasNodePermission(node, permissionSet)) {
      filtered.push(node);
    }
  }

  return cleanDividers(filtered);
}

function findFirstPath(nodes = []) {
  for (const node of nodes) {
    if (!node || node.divider) continue;

    if (node.path) return node.path;

    if (Array.isArray(node.items)) {
      const found = findFirstPath(node.items);
      if (found) return found;
    }
  }

  return null;
}

const knownPaths = (() => {
  const set = new Set(Object.keys(friendlyMap).map(normalizePath));

  collectPaths(menuItems, set);
  collectPaths(userMenuItems, set);

  return set;
})();

const dynamicFriendlyRoutes = [
  { pattern: /^\/salud\/pacientes\/[^/]+$/, label: 'Ficha del paciente' },
  { pattern: /^\/salud\/atenciones\/[^/]+$/, label: 'Ficha de atención' },
];

function getDynamicFriendlyLabel(path) {
  const normalized = normalizePath(path);
  return dynamicFriendlyRoutes.find(({ pattern }) => pattern.test(normalized))?.label || '';
}

function isKnownPath(path) {
  const normalized = normalizePath(path);
  return (
    knownPaths.has(normalized) ||
    dynamicFriendlyRoutes.some(({ pattern }) => pattern.test(normalized))
  );
}

function isActionSegment(seg = '') {
  const normalized = String(seg).toLowerCase();

  return [
    'crear',
    'editar',
    'edit',
    'ver',
    'detalle',
    'detalles',
    'nuevo',
    'nueva',
    'actualizar',
    'update',
    'emitir',
  ].includes(normalized);
}


/* ────────────────────────────── MENÚ INTELIGENTE ────────────────────────────── */

function normalizeSearchText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function getUserStorageId(user) {
  return String(
    user?.id ||
      user?.user_id ||
      user?.usuario_id ||
      user?.uuid ||
      user?.email ||
      user?.rut ||
      'usuario'
  ).trim();
}

function getFavoriteStorageKey(user, tenant) {
  const userId = getUserStorageId(user);
  const tenantId = getTenantId(tenant) || 'sin-tenant';

  return `integranube_menu_favoritos:${userId}:${tenantId}`;
}

function buildMenuKeyFromRoute(routePath) {
  const normalizedRoute = normalizePath(routePath);

  if (!normalizedRoute) return '';
  if (normalizedRoute === '/') return 'home';

  const menuKey = normalizedRoute
    .replace(/^\/+/, '')
    .split('/')
    .filter(Boolean)
    .map((segment) =>
      segment
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9_-]+/g, '-')
        .replace(/^-+|-+$/g, '')
    )
    .filter(Boolean)
    .join('.');

  return (menuKey || 'menu').slice(0, 180);
}

function normalizeFavoriteRecords(value) {
  const list = Array.isArray(value) ? value : [];
  const routes = [];
  const menuKeyByRoute = {};
  const records = [];
  const seenRoutes = new Set();

  for (const entry of list) {
    const rawRoute =
      typeof entry === 'string'
        ? entry
        : entry?.routePath ||
          entry?.route_path ||
          entry?.path ||
          '';

    const routePath = normalizePath(rawRoute);

    if (!routePath || seenRoutes.has(routePath)) continue;

    const menuKey =
      typeof entry === 'object' && entry
        ? String(
            entry.menuKey ||
              entry.menu_key ||
              buildMenuKeyFromRoute(routePath)
          ).trim()
        : buildMenuKeyFromRoute(routePath);

    seenRoutes.add(routePath);
    routes.push(routePath);
    menuKeyByRoute[routePath] =
      menuKey || buildMenuKeyFromRoute(routePath);

    records.push({
      menuKey: menuKeyByRoute[routePath],
      routePath,
      sortOrder:
        typeof entry === 'object' &&
        entry &&
        Number.isInteger(Number(entry.sortOrder ?? entry.sort_order))
          ? Number(entry.sortOrder ?? entry.sort_order)
          : records.length + 1,
    });
  }

  return {
    routes,
    menuKeyByRoute,
    records,
  };
}

function flattenMenuLeaves(nodes = [], moduleLabel = 'General', parents = [], acc = []) {
  for (const node of nodes) {
    if (!node || node.divider) continue;

    const hasChildren = Array.isArray(node.items) && node.items.length > 0;

    if (hasChildren) {
      flattenMenuLeaves(node.items, moduleLabel, [...parents, node.label], acc);
      continue;
    }

    if (!node.path) continue;

    const hierarchy = [...parents, node.label].filter(Boolean);
    const groupLabel = parents.length > 0 ? parents[parents.length - 1] : moduleLabel;

    acc.push({
      ...node,
      moduleLabel,
      groupLabel,
      hierarchy,
      favoriteKey: normalizePath(node.path),
      searchIndex: normalizeSearchText(
        [
          node.label,
          moduleLabel,
          groupLabel,
          hierarchy.join(' '),
          node.path,
          ...(Array.isArray(node.keywords) ? node.keywords : []),
        ].join(' ')
      ),
    });
  }

  return acc;
}

function getAuthorizedLeaves(modules = []) {
  const leaves = [];

  for (const module of modules) {
    if (!module || module.divider) continue;

    if (Array.isArray(module.items) && module.items.length > 0) {
      flattenMenuLeaves(module.items, module.label, [], leaves);
    } else if (module.path) {
      flattenMenuLeaves([module], module.label || 'General', [], leaves);
    }
  }

  const seen = new Set();

  return leaves.filter((item) => {
    if (!item.favoriteKey || seen.has(item.favoriteKey)) return false;
    seen.add(item.favoriteKey);
    return true;
  });
}

function scoreSearchResult(item, normalizedQuery) {
  if (!normalizedQuery) return 0;

  const label = normalizeSearchText(item.label);
  const moduleLabel = normalizeSearchText(item.moduleLabel);
  const groupLabel = normalizeSearchText(item.groupLabel);

  if (label === normalizedQuery) return 100;
  if (label.startsWith(normalizedQuery)) return 80;
  if (label.includes(normalizedQuery)) return 60;
  if (groupLabel.startsWith(normalizedQuery)) return 45;
  if (moduleLabel.startsWith(normalizedQuery)) return 40;
  if (item.searchIndex.includes(normalizedQuery)) return 20;

  return 0;
}


function clearAuthStoragePreservingMenuPreferences() {
  try {
    const preservedEntries = [];

    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);

      if (key?.startsWith('integranube_menu_favoritos:')) {
        preservedEntries.push([key, localStorage.getItem(key)]);
      }
    }

    localStorage.clear();

    preservedEntries.forEach(([key, value]) => {
      if (value !== null) localStorage.setItem(key, value);
    });
  } catch (_) {
    try {
      localStorage.clear();
    } catch (_) {}
  }
}

/* ────────────────────────────── COMPONENTE ────────────────────────────── */

const Navbar = ({ onLogout }) => {
  const [modulesMenuOpen, setModulesMenuOpen] = useState(false);
  const [favoritesMenuOpen, setFavoritesMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileDrawerSection, setMobileDrawerSection] = useState('modules');
  const [expanded, setExpanded] = useState({});
  const [megaTrail, setMegaTrail] = useState([]);
  const [favoriteKeys, setFavoriteKeys] = useState([]);
  const [favoriteMenuKeyByRoute, setFavoriteMenuKeyByRoute] = useState({});
  const [favoritePendingKeys, setFavoritePendingKeys] = useState([]);
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [permissionsReady, setPermissionsReady] = useState(false);
  const [netStatus, setNetStatus] = useState(NET_OK);

  const searchInputRef = useRef(null);
  const megaHoverTimerRef = useRef(null);
  const skipFavoriteSaveRef = useRef(false);
  const favoritePendingRef = useRef(new Set());

  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width:960px)');
  const currentPath = normalizePath(location.pathname);

  const permissionSet = useMemo(() => new Set(normalizePermissionList(permissions)), [permissions]);

  const authorizedMenuItems = useMemo(() => {
    if (!permissionsReady) return [];
    return filterMenuByPermissions(menuItems, permissionSet);
  }, [permissionSet, permissionsReady]);

  const authorizedUserMenuItems = useMemo(() => {
    const sourceItems = permissionsReady
      ? userMenuItems
      : userMenuItems.filter((item) => item.public || item.divider);

    return filterMenuByPermissions(sourceItems, permissionSet);
  }, [permissionSet, permissionsReady]);

  const authorizedLeaves = useMemo(
    () => getAuthorizedLeaves(authorizedMenuItems),
    [authorizedMenuItems]
  );

  // La ruta raíz es la única portada del sistema.
  // App.js decide si muestra el dashboard administrativo,
  // el portal de aplicaciones u otra vista según el perfil.
  const homeDestination = '/';

  const favoriteStorageKey = useMemo(
    () => getFavoriteStorageKey(user, tenant),
    [user, tenant]
  );

  const favoriteSet = useMemo(() => new Set(favoriteKeys), [favoriteKeys]);
  const favoritePendingSet = useMemo(
    () => new Set(favoritePendingKeys),
    [favoritePendingKeys]
  );

  const favoriteItems = useMemo(
    () =>
      favoriteKeys
        .map((favoriteKey) =>
          authorizedLeaves.find(
            (item) => item.favoriteKey === favoriteKey
          )
        )
        .filter(Boolean),
    [authorizedLeaves, favoriteKeys]
  );

  const normalizedQuery = useMemo(() => normalizeSearchText(searchText), [searchText]);

  const searchResults = useMemo(() => {
    if (!normalizedQuery) {
      const preferred = favoriteItems.length > 0 ? favoriteItems : authorizedLeaves;
      return preferred.slice(0, 12);
    }

    return authorizedLeaves
      .map((item) => ({
        item,
        score: scoreSearchResult(item, normalizedQuery),
      }))
      .filter((result) => result.score > 0)
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return a.item.label.localeCompare(b.item.label, 'es');
      })
      .slice(0, 16)
      .map((result) => result.item);
  }, [authorizedLeaves, favoriteItems, normalizedQuery]);


  const authorizedLeafMap = useMemo(
    () =>
      new Map(
        authorizedLeaves.map((item) => [
          normalizePath(item.path),
          item,
        ])
      ),
    [authorizedLeaves]
  );

  const megaColumns = useMemo(() => {
    const columns = [
      {
        key: 'root',
        title: 'Módulos',
        subtitle: 'Selecciona un módulo',
        nodes: authorizedMenuItems,
        selectedNode: megaTrail[0] || null,
      },
    ];

    megaTrail.forEach((selectedNode, level) => {
      const children = cleanDividers(selectedNode?.items || []);

      if (children.length === 0) return;

      columns.push({
        key: `${level + 1}-${selectedNode.label}`,
        title: selectedNode.label,
        subtitle:
          level === 0
            ? 'Selecciona una sección o función'
            : 'Selecciona una opción',
        nodes: children,
        selectedNode: megaTrail[level + 1] || null,
      });
    });

    return columns;
  }, [authorizedMenuItems, megaTrail]);

  const hideMobileFabPaths = ['/apps/inventario', '/apps/inventario-rapido'];

  const hideMobileFab = hideMobileFabPaths.some((path) => {
    const normalized = normalizePath(path);
    return currentPath === normalized || currentPath.startsWith(`${normalized}/`);
  });

  const closeFloatingMenus = () => {
    if (megaHoverTimerRef.current) {
      window.clearTimeout(megaHoverTimerRef.current);
      megaHoverTimerRef.current = null;
    }

    setModulesMenuOpen(false);
    setFavoritesMenuOpen(false);
    setUserMenuOpen(false);
    setSearchOpen(false);
    setMegaTrail([]);
  };

  const openSearch = () => {
    setModulesMenuOpen(false);
    setFavoritesMenuOpen(false);
    setUserMenuOpen(false);
    setSearchOpen(true);

    window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
  };

  useEffect(() => {
    let mounted = true;

    const loadAuthContext = async () => {
      const storedUser = safeJsonParse(localStorage.getItem('user'), null);
      const storedTenant = safeJsonParse(localStorage.getItem('tenant'), null);

      if (mounted) {
        setUser(storedUser);
        setTenant(storedTenant);
      }

      const cachedPermissions = normalizePermissionList(
        storedTenant?.permissions || storedTenant?.permisos || []
      );

      if (cachedPermissions.length > 0 && mounted) {
        setPermissions(cachedPermissions);
        setPermissionsReady(true);
      } else if (mounted) {
        setPermissions([]);
        setPermissionsReady(false);
      }

      try {
        const response = await api.get('/auth/me');
        const payload = response?.data || {};

        const allowedTenants = Array.isArray(payload?.allowedTenants)
          ? payload.allowedTenants
          : Array.isArray(payload?.allowed_tenants)
            ? payload.allowed_tenants
            : [];

        const activeTenant = resolveCurrentTenant(allowedTenants, storedTenant);

        if (!mounted) return;

        if (!activeTenant) {
          setPermissions([]);
          setPermissionsReady(true);
          return;
        }

        const tenantPermissions = normalizePermissionList(
          activeTenant.permissions || activeTenant.permisos || []
        );

        const mergedTenant = {
          ...(storedTenant || {}),
          ...activeTenant,
          tenantid: getTenantId(activeTenant) || getTenantId(storedTenant),
          permissions: tenantPermissions,
        };

        const tenantPreferences =
          payload?.tenantPreferences ||
          payload?.tenant_preferences ||
          null;

        const preferencesTenantId = getTenantId(tenantPreferences);
        const activeTenantId = getTenantId(mergedTenant);

        const hasServerFavorites =
          tenantPreferences &&
          preferencesTenantId &&
          activeTenantId &&
          preferencesTenantId === activeTenantId &&
          (
            Array.isArray(tenantPreferences.menuFavorites) ||
            Array.isArray(tenantPreferences.menu_favorites)
          );

        const serverFavorites = hasServerFavorites
          ? normalizeFavoriteRecords(
              tenantPreferences.menuFavorites ||
                tenantPreferences.menu_favorites ||
                []
            )
          : null;

        const resolvedUser = payload?.user || storedUser || null;

        setUser(resolvedUser);
        setTenant(mergedTenant);
        setPermissions(tenantPermissions);
        setPermissionsReady(true);

        if (serverFavorites) {
          skipFavoriteSaveRef.current = true;
          setFavoriteKeys(serverFavorites.routes);
          setFavoriteMenuKeyByRoute(
            serverFavorites.menuKeyByRoute
          );
        }

        try {
          if (payload?.user) {
            localStorage.setItem('user', JSON.stringify(payload.user));
          }

          localStorage.setItem('tenant', JSON.stringify(mergedTenant));

          if (serverFavorites) {
            localStorage.setItem(
              getFavoriteStorageKey(resolvedUser, mergedTenant),
              JSON.stringify(serverFavorites.records)
            );
          }
        } catch (_) {}
      } catch (error) {
        console.warn('[Navbar] No se pudieron actualizar permisos desde /auth/me', error);

        if (mounted) {
          setPermissionsReady(true);
        }
      }
    };

    loadAuthContext();

    const onStorage = (event) => {
      const watchedKeys = ['user', 'tenant', 'tenantid', AUTH_EVENT_KEY];

      if (!event.key || watchedKeys.includes(event.key)) {
        loadAuthContext();
      }
    };

    window.addEventListener('storage', onStorage);

    return () => {
      mounted = false;
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  useEffect(() => {
    skipFavoriteSaveRef.current = true;

    const storedFavorites = normalizeFavoriteRecords(
      safeJsonParse(
        localStorage.getItem(favoriteStorageKey),
        []
      )
    );

    setFavoriteKeys(storedFavorites.routes);
    setFavoriteMenuKeyByRoute(
      storedFavorites.menuKeyByRoute
    );

    const syncFavorites = (event) => {
      if (event.key !== favoriteStorageKey) return;

      const nextFavorites = normalizeFavoriteRecords(
        safeJsonParse(event.newValue, [])
      );

      skipFavoriteSaveRef.current = true;
      setFavoriteKeys(nextFavorites.routes);
      setFavoriteMenuKeyByRoute(
        nextFavorites.menuKeyByRoute
      );
    };

    window.addEventListener('storage', syncFavorites);

    return () => {
      window.removeEventListener('storage', syncFavorites);
    };
  }, [favoriteStorageKey]);

  useEffect(() => {
    if (skipFavoriteSaveRef.current) {
      skipFavoriteSaveRef.current = false;
      return;
    }

    const cachedFavorites = favoriteKeys.map(
      (routePath, index) => ({
        menuKey:
          favoriteMenuKeyByRoute[routePath] ||
          buildMenuKeyFromRoute(routePath),
        routePath,
        sortOrder: index + 1,
      })
    );

    try {
      localStorage.setItem(
        favoriteStorageKey,
        JSON.stringify(cachedFavorites)
      );
    } catch (_) {}
  }, [
    favoriteKeys,
    favoriteMenuKeyByRoute,
    favoriteStorageKey,
  ]);

  useEffect(() => {
    const checkConnectivity = async () => {
      const start = performance.now();

      try {
        const res = await fetch('/status', {
          method: 'HEAD',
          cache: 'no-store',
        });

        const ms = performance.now() - start;

        if (!res.ok) {
          setNetStatus(NET_DOWN);
        } else if (ms >= 180) {
          setNetStatus(NET_SLOW);
        } else {
          setNetStatus(NET_OK);
        }
      } catch {
        setNetStatus(NET_DOWN);
      }
    };

    checkConnectivity();

    const id = setInterval(checkConnectivity, 60000);

    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    return () => {
      if (megaHoverTimerRef.current) {
        window.clearTimeout(megaHoverTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleShortcut = (event) => {
      const isSearchShortcut =
        (event.ctrlKey || event.metaKey) && String(event.key).toLowerCase() === 'k';

      if (!isSearchShortcut) return;

      event.preventDefault();
      openSearch();
    };

    window.addEventListener('keydown', handleShortcut);

    return () => {
      window.removeEventListener('keydown', handleShortcut);
    };
  });

  useEffect(() => {
    closeFloatingMenus();
    setDrawerOpen(false);
    setExpanded({});
    setSearchText('');
  }, [location.pathname]);

  useEffect(() => {
    if (hideMobileFab && drawerOpen) {
      setDrawerOpen(false);
    }
  }, [hideMobileFab, drawerOpen]);

  const toggleFavorite = async (favoriteKey, event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    const routePath = normalizePath(favoriteKey);
    if (!routePath) return;

    if (favoritePendingRef.current.has(routePath)) return;

    const wasFavorite = favoriteSet.has(routePath);
    const previousMenuKey =
      favoriteMenuKeyByRoute[routePath] || null;
    const menuKey =
      previousMenuKey || buildMenuKeyFromRoute(routePath);

    if (!menuKey) return;

    favoritePendingRef.current.add(routePath);
    setFavoritePendingKeys(
      Array.from(favoritePendingRef.current)
    );

    if (wasFavorite) {
      setFavoriteKeys((current) =>
        current.filter((key) => key !== routePath)
      );
    } else {
      setFavoriteKeys((current) =>
        current.includes(routePath)
          ? current
          : [...current, routePath]
      );

      setFavoriteMenuKeyByRoute((current) => ({
        ...current,
        [routePath]: menuKey,
      }));
    }

    try {
      if (wasFavorite) {
        await api.delete(
          `/auth/preferences/favorites/${encodeURIComponent(
            menuKey
          )}`
        );

        setFavoriteMenuKeyByRoute((current) => {
          const next = { ...current };
          delete next[routePath];
          return next;
        });
      } else {
        const response = await api.put(
          `/auth/preferences/favorites/${encodeURIComponent(
            menuKey
          )}`,
          {
            routePath,
          }
        );

        const savedFavorite =
          response?.data?.favorite || null;

        const savedRoute = normalizePath(
          savedFavorite?.routePath ||
            savedFavorite?.route_path ||
            routePath
        );

        const savedMenuKey = String(
          savedFavorite?.menuKey ||
            savedFavorite?.menu_key ||
            menuKey
        ).trim();

        if (savedRoute && savedMenuKey) {
          setFavoriteMenuKeyByRoute((current) => ({
            ...current,
            [savedRoute]: savedMenuKey,
          }));
        }
      }
    } catch (error) {
      console.error(
        '[Navbar] No se pudo actualizar el favorito:',
        {
          routePath,
          menuKey,
          action: wasFavorite ? 'delete' : 'put',
          status: error?.response?.status,
          data: error?.response?.data,
          message: error?.message,
        }
      );

      if (wasFavorite) {
        setFavoriteKeys((current) =>
          current.includes(routePath)
            ? current
            : [...current, routePath]
        );
      } else {
        setFavoriteKeys((current) =>
          current.filter((key) => key !== routePath)
        );

        if (!previousMenuKey) {
          setFavoriteMenuKeyByRoute((current) => {
            const next = { ...current };
            delete next[routePath];
            return next;
          });
        }
      }
    } finally {
      favoritePendingRef.current.delete(routePath);
      setFavoritePendingKeys(
        Array.from(favoritePendingRef.current)
      );
    }
  };

  const clearTenantSelection = () => {
    try {
      localStorage.removeItem('tenant');
      localStorage.removeItem('tenantid');

      localStorage.setItem(
        AUTH_EVENT_KEY,
        JSON.stringify({
          type: 'switch_tenant',
          at: Date.now(),
        })
      );
    } catch (_) {}

    try {
      sessionStorage.removeItem('tenant');
      sessionStorage.removeItem('tenantid');
    } catch (_) {}

    try {
      delete api.defaults.headers.common.tenantid;
      delete api.defaults.headers.common.tenantId;
      delete api.defaults.headers.common.TenantId;
    } catch (_) {}

    favoritePendingRef.current.clear();
    setFavoritePendingKeys([]);
    setFavoriteKeys([]);
    setFavoriteMenuKeyByRoute({});
    setTenant(null);
    setPermissions([]);
    setPermissionsReady(false);
  };

  const openMobileDrawer = () => {
    setMobileDrawerSection('modules');
    setDrawerOpen(true);
  };

  const closeMobileDrawer = () => {
    setDrawerOpen(false);
    setSearchText('');
  };

  const handleChangeTenant = () => {
    closeFloatingMenus();
    closeMobileDrawer();
    setExpanded({});

    clearTenantSelection();

    window.location.replace('/login?switchTenant=1');
  };

  const handleLogout = () => {
    clearAuthStoragePreservingMenuPreferences();

    try {
      sessionStorage.clear();
    } catch (_) {}

    try {
      delete api.defaults.headers.common.Authorization;
      delete api.defaults.headers.common.tenantid;
      delete api.defaults.headers.common.tenantId;
      delete api.defaults.headers.common.TenantId;
    } catch (_) {}

    setPermissions([]);
    setPermissionsReady(false);

    if (typeof onLogout === 'function') {
      onLogout();
      return;
    }

    window.location.replace('/login');
  };

  const handleUserMenuAction = (item) => {
    if (!item || item.divider) return;

    if (item.label === 'Cerrar sesión') {
      handleLogout();
      return;
    }

    if (item.label === 'Cambiar empresa') {
      handleChangeTenant();
      return;
    }

    closeFloatingMenus();
    closeMobileDrawer();

    if (item.path) {
      navigate(item.path);
    }
  };

  const handleNavigate = (path) => {
    if (!path) return;

    closeFloatingMenus();
    closeMobileDrawer();
    navigate(path);
  };

  const isActivePath = (path) => {
    if (!path) return false;

    const current = normalizePath(location.pathname);
    const target = normalizePath(path);

    if (target === '/') {
      return current === '/';
    }

    return current === target || current.startsWith(`${target}/`);
  };

  const isActiveTree = (node) => {
    if (!node) return false;

    if (node.path) {
      return isActivePath(node.path);
    }

    if (Array.isArray(node.items)) {
      return node.items.some(isActiveTree);
    }

    return false;
  };


  const getMenuNodeKey = (node) =>
    normalizePath(node?.path) || String(node?.label || '').trim();

  const isSelectedMegaNode = (node, columnIndex) => {
    const selectedNode = megaTrail[columnIndex];

    return (
      !!selectedNode &&
      getMenuNodeKey(selectedNode) === getMenuNodeKey(node)
    );
  };

  const handleMegaNode = (node, columnIndex) => {
    if (!node || node.divider) return;

    const hasChildren = Array.isArray(node.items) && node.items.length > 0;

    if (hasChildren) {
      setMegaTrail((current) => [
        ...current.slice(0, columnIndex),
        node,
      ]);
      return;
    }

    if (node.path) {
      handleNavigate(node.path);
    }
  };

  const previewMegaNode = (node, columnIndex) => {
    if (!node || node.divider) return;

    const hasChildren = Array.isArray(node.items) && node.items.length > 0;

    setMegaTrail((current) => {
      if (hasChildren) {
        const currentNode = current[columnIndex];

        if (
          currentNode &&
          getMenuNodeKey(currentNode) === getMenuNodeKey(node)
        ) {
          return current;
        }

        return [
          ...current.slice(0, columnIndex),
          node,
        ];
      }

      // Si se pasa sobre una función final, se cierran las columnas
      // posteriores sin navegar automáticamente.
      return current.slice(0, columnIndex);
    });
  };

  const scheduleMegaPreview = (node, columnIndex) => {
    if (megaHoverTimerRef.current) {
      window.clearTimeout(megaHoverTimerRef.current);
    }

    megaHoverTimerRef.current = window.setTimeout(() => {
      previewMegaNode(node, columnIndex);
      megaHoverTimerRef.current = null;
    }, 110);
  };

  const cancelMegaPreview = () => {
    if (!megaHoverTimerRef.current) return;

    window.clearTimeout(megaHoverTimerRef.current);
    megaHoverTimerRef.current = null;
  };

  const renderFavoriteButton = (item, compact = false) => {
    const isFavorite = favoriteSet.has(item.favoriteKey);
    const isSaving = favoritePendingSet.has(item.favoriteKey);

    return (
      <FavoriteButton
        type="button"
        $compact={compact}
        $active={isFavorite}
        disabled={isSaving}
        aria-busy={isSaving}
        title={
          isSaving
            ? 'Guardando favorito...'
            : isFavorite
              ? 'Quitar de favoritos'
              : 'Agregar a favoritos'
        }
        aria-label={
          isFavorite
            ? `Quitar ${item.label} de favoritos`
            : `Agregar ${item.label} a favoritos`
        }
        onClick={(event) =>
          toggleFavorite(item.favoriteKey, event)
        }
      >
        {isFavorite ? (
          <StarIcon fontSize="small" />
        ) : (
          <StarBorderIcon fontSize="small" />
        )}
      </FavoriteButton>
    );
  };

  const renderFunctionRow = (item, variant = 'default') => (
    <FunctionRow key={`${variant}-${item.favoriteKey}`} $active={isActivePath(item.path)}>
      <FunctionLinkButton type="button" onClick={() => handleNavigate(item.path)}>
        <FunctionIcon>{item.icon}</FunctionIcon>

        <FunctionText>
          <strong>{item.label}</strong>
          <small>
            {item.moduleLabel}
            {item.groupLabel && item.groupLabel !== item.moduleLabel
              ? ` › ${item.groupLabel}`
              : ''}
          </small>
        </FunctionText>
      </FunctionLinkButton>

      {renderFavoriteButton(item, variant === 'compact')}
    </FunctionRow>
  );

  const renderDrawerNodes = (nodes, level = 0, parentKey = '') =>
    (nodes || []).map((node, idx) => {
      const key = `${parentKey}${node.label || 'div'}-${level}-${idx}`;
      const hasChildren = Array.isArray(node.items) && node.items.length > 0;

      if (node.divider) {
        return <MuiDivider key={key} sx={{ ml: level ? 7 : 0 }} />;
      }

      if (!hasChildren) {
        const favoriteKey = normalizePath(node.path);
        const isFavorite = favoriteSet.has(favoriteKey);

        return (
          <DrawerLeafRow key={key}>
            <ListItemButton
              component={RouterLink}
              to={node.path || '#'}
              selected={isActivePath(node.path)}
              sx={{ pl: 2 + level * 3, pr: 7 }}
              onClick={(event) => {
                if (!node.path) {
                  event.preventDefault();
                  return;
                }

                closeMobileDrawer();
              }}
            >
              <ListItemIcon>{node.icon}</ListItemIcon>
              <ListItemText primary={node.label} />
            </ListItemButton>

            <DrawerFavoriteButton
              type="button"
              $active={isFavorite}
              disabled={favoritePendingSet.has(favoriteKey)}
              aria-busy={favoritePendingSet.has(favoriteKey)}
              title={
                favoritePendingSet.has(favoriteKey)
                  ? 'Guardando favorito...'
                  : isFavorite
                    ? 'Quitar de favoritos'
                    : 'Agregar a favoritos'
              }
              onClick={(event) =>
                toggleFavorite(favoriteKey, event)
              }
            >
              {isFavorite ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
            </DrawerFavoriteButton>
          </DrawerLeafRow>
        );
      }

      const open = !!expanded[key];

      return (
        <Fragment key={key}>
          <ListItemButton
            onClick={() =>
              setExpanded((state) => ({
                ...state,
                [key]: !open,
              }))
            }
            sx={{ pl: 2 + level * 3 }}
            selected={isActiveTree(node)}
          >
            <ListItemIcon>{node.icon}</ListItemIcon>
            <ListItemText primary={node.label} />
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ListItemButton>

          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {renderDrawerNodes(node.items, level + 1, `${key}>`)}
            </List>
          </Collapse>
        </Fragment>
      );
    });

  const ConnectionIndicator = ({ compact = false }) => (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      title={
        netStatus === NET_OK
          ? 'Conexión óptima'
          : netStatus === NET_SLOW
            ? 'Conexión lenta'
            : 'Sin conexión'
      }
    >
      {netStatus === NET_OK && <WifiIcon sx={{ color: colorMap[NET_OK] }} fontSize="small" />}
      {netStatus === NET_SLOW && (
        <NetworkCheckIcon sx={{ color: colorMap[NET_SLOW] }} fontSize="small" />
      )}
      {netStatus === NET_DOWN && (
        <WifiOffIcon sx={{ color: colorMap[NET_DOWN] }} fontSize="small" />
      )}

      {!compact && (
        <Typography variant="body2" sx={{ color: colorMap[netStatus] }}>
          {labelMap[netStatus]}
        </Typography>
      )}
    </Stack>
  );

  const pathnames = location.pathname.split('/').filter(Boolean);

  const makeCrumbs = () => {
    const crumbs = [];
    let agg = '';
    let lastValidPath = homeDestination || '/';

    const explicitFrom =
      normalizePath(location.state?.breadcrumbFrom) ||
      normalizePath(location.state?.from) ||
      '';

    pathnames.forEach((seg, index) => {
      agg += `/${seg}`;

      const normalizedAgg = normalizePath(agg);
      const isLast = index === pathnames.length - 1;

      const label =
        friendlyMap[normalizedAgg] ||
        getDynamicFriendlyLabel(normalizedAgg) ||
        (isNumericSegment(seg) ? `#${seg}` : toTitle(seg));

      const routeExists = isKnownPath(normalizedAgg);
      const actionSeg = isActionSegment(seg);

      let to = normalizedAgg;

      if (!routeExists) {
        if (actionSeg && explicitFrom) {
          to = explicitFrom;
        } else {
          to = lastValidPath;
        }
      }

      crumbs.push({
        key: `${normalizedAgg}-${index}`,
        to,
        label,
        isLast,
        clickable: !isLast && !!to,
      });

      if (routeExists) {
        lastValidPath = normalizedAgg;
      }
    });

    return crumbs;
  };

  return (
    <Fragment>
      <StyledAppBar position="static" elevation={0}>
        <TopToolbar>
          <LogoLink to={homeDestination} onClick={closeFloatingMenus}>
            <LogoImg src={logo} alt="IntegraNube" />
          </LogoLink>

          {!isMobile && (
            <>
              <ClickAwayListener
                onClickAway={() => {
                  cancelMegaPreview();
                  setModulesMenuOpen(false);
                  setMegaTrail([]);
                }}
              >
                <MegaMenuRoot>
                  <TopActionButton
                    type="button"
                    $active={modulesMenuOpen}
                    onClick={() => {
                      const nextOpen = !modulesMenuOpen;

                      setModulesMenuOpen(nextOpen);
                      setFavoritesMenuOpen(false);
                      setUserMenuOpen(false);
                      setSearchOpen(false);

                      if (!nextOpen) {
                        cancelMegaPreview();
                        setMegaTrail([]);
                      }
                    }}
                  >
                    <AppsIcon fontSize="small" />
                    <span>Módulos</span>
                    {modulesMenuOpen ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </TopActionButton>

                  {modulesMenuOpen && (
                    <ProgressiveMenuPanel
                      style={{
                        '--mega-column-count': megaColumns.length,
                      }}
                      onMouseLeave={cancelMegaPreview}
                    >


                      {authorizedMenuItems.length > 0 ? (
                        <ProgressiveColumns>
                          {megaColumns.map((column, columnIndex) => (
                            <ProgressiveColumn key={column.key}>
                              {columnIndex > 0 && (
                                <ProgressiveColumnHeader>
                                  <strong>{column.title}</strong>
                                  <span>{column.subtitle}</span>
                                </ProgressiveColumnHeader>
                              )}

                              <ProgressiveItems $firstColumn={columnIndex === 0}>
                                {column.nodes.map((node, nodeIndex) => {
                                  if (node.divider) {
                                    return (
                                      <ProgressiveDivider
                                        key={`progressive-divider-${column.key}-${nodeIndex}`}
                                      />
                                    );
                                  }

                                  const hasChildren =
                                    Array.isArray(node.items) &&
                                    node.items.length > 0;

                                  const selected = isSelectedMegaNode(
                                    node,
                                    columnIndex
                                  );

                                  const leafItem = !hasChildren
                                    ? authorizedLeafMap.get(
                                        normalizePath(node.path)
                                      )
                                    : null;

                                  return (
                                    <ProgressiveItemRow
                                      key={`${column.key}-${getMenuNodeKey(node)}-${nodeIndex}`}
                                      $selected={selected}
                                      $active={
                                        !hasChildren &&
                                        isActivePath(node.path)
                                      }
                                      onMouseEnter={() =>
                                        scheduleMegaPreview(node, columnIndex)
                                      }
                                    >
                                      <ProgressiveItemButton
                                        type="button"
                                        onClick={() =>
                                          handleMegaNode(node, columnIndex)
                                        }
                                      >
                                        <ProgressiveItemIcon>
                                          {node.icon}
                                        </ProgressiveItemIcon>

                                        <ProgressiveItemText>
                                          <strong>{node.label}</strong>

                                          {hasChildren && (
                                            <small>
                                              {getAuthorizedLeaves([node]).length}{' '}
                                              {getAuthorizedLeaves([node]).length === 1
                                                ? 'función'
                                                : 'funciones'}
                                            </small>
                                          )}
                                        </ProgressiveItemText>

                                        {hasChildren && (
                                          <ProgressiveArrow>
                                            <ChevronRightIcon fontSize="small" />
                                          </ProgressiveArrow>
                                        )}
                                      </ProgressiveItemButton>

                                      {!hasChildren && leafItem && (
                                        <ProgressiveFavoriteWrap
                                          $active={favoriteSet.has(
                                            leafItem.favoriteKey
                                          )}
                                        >
                                          {renderFavoriteButton(
                                            leafItem,
                                            true
                                          )}
                                        </ProgressiveFavoriteWrap>
                                      )}
                                    </ProgressiveItemRow>
                                  );
                                })}
                              </ProgressiveItems>
                            </ProgressiveColumn>
                          ))}
                        </ProgressiveColumns>
                      ) : (
                        <EmptyState>
                          No hay módulos disponibles para los permisos actuales.
                        </EmptyState>
                      )}
                    </ProgressiveMenuPanel>
                  )}
                </MegaMenuRoot>
              </ClickAwayListener>

              <ClickAwayListener onClickAway={() => setFavoritesMenuOpen(false)}>
                <FloatingRoot>
                  <TopActionButton
                    type="button"
                    $active={favoritesMenuOpen}
                    onClick={() => {
                      setFavoritesMenuOpen((open) => !open);
                      setModulesMenuOpen(false);
                      setUserMenuOpen(false);
                      setSearchOpen(false);
                    }}
                  >
                    <StarIcon fontSize="small" />
                    <span>Favoritos</span>
                    {favoriteItems.length > 0 && (
                      <CountBadge>{favoriteItems.length}</CountBadge>
                    )}
                  </TopActionButton>

                  {favoritesMenuOpen && (
                    <FavoritesPanel>
                      <PanelHeader>
                        <div>
                          <strong>Accesos rápidos</strong>
                          <span>Tus funciones favoritas para esta empresa.</span>
                        </div>
                      </PanelHeader>

                      {favoriteItems.length > 0 ? (
                        <PanelList>
                          {favoriteItems.map((item) =>
                            renderFunctionRow(item, 'favorite')
                          )}
                        </PanelList>
                      ) : (
                        <EmptyState>
                          Marca una estrella en el megamenú o en el buscador para
                          crear tus accesos rápidos.
                        </EmptyState>
                      )}
                    </FavoritesPanel>
                  )}
                </FloatingRoot>
              </ClickAwayListener>

              <ClickAwayListener onClickAway={() => setSearchOpen(false)}>
                <SearchArea>
                  <SearchBox $open={searchOpen}>
                    <SearchIcon fontSize="small" />

                    <SearchInput
                      ref={searchInputRef}
                      value={searchText}
                      onChange={(event) => {
                        setSearchText(event.target.value);
                        setSearchOpen(true);
                      }}
                      onFocus={() => {
                        setSearchOpen(true);
                        setModulesMenuOpen(false);
                        setFavoritesMenuOpen(false);
                        setUserMenuOpen(false);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Escape') {
                          setSearchOpen(false);
                          searchInputRef.current?.blur();
                        }

                        if (event.key === 'Enter' && searchResults[0]?.path) {
                          handleNavigate(searchResults[0].path);
                        }
                      }}
                      placeholder="Buscar funciones en IntegraNube…"
                      aria-label="Buscar funciones"
                    />

                    {!searchText && <ShortcutHint>Ctrl K</ShortcutHint>}

                    {searchText && (
                      <ClearSearchButton
                        type="button"
                        onClick={() => {
                          setSearchText('');
                          searchInputRef.current?.focus();
                        }}
                        aria-label="Limpiar búsqueda"
                      >
                        <CloseIcon fontSize="small" />
                      </ClearSearchButton>
                    )}
                  </SearchBox>

                  {searchOpen && (
                    <SearchPanel>
                      <SearchPanelTitle>
                        {normalizedQuery
                          ? `Resultados para “${searchText.trim()}”`
                          : favoriteItems.length > 0
                            ? 'Tus favoritos'
                            : 'Funciones disponibles'}
                      </SearchPanelTitle>

                      {searchResults.length > 0 ? (
                        <PanelList>
                          {searchResults.map((item) =>
                            renderFunctionRow(item, 'search')
                          )}
                        </PanelList>
                      ) : (
                        <EmptyState>
                          No encontramos una función autorizada con ese nombre.
                        </EmptyState>
                      )}
                    </SearchPanel>
                  )}
                </SearchArea>
              </ClickAwayListener>

              <DesktopConnection>
                <ConnectionIndicator />
              </DesktopConnection>
            </>
          )}

          {isMobile && (
            <MobileConnection>
              <ConnectionIndicator compact />
            </MobileConnection>
          )}

          <Spacer />

          <ClickAwayListener onClickAway={() => setUserMenuOpen(false)}>
            <FloatingRoot>
              <UserBlock
                onClick={() => {
                  setUserMenuOpen((open) => !open);
                  setModulesMenuOpen(false);
                  setFavoritesMenuOpen(false);
                  setSearchOpen(false);
                }}
              >
                <AccountCircleIcon fontSize="large" />

                <UserText>
                  <strong>{getDisplayName(user, 'Usuario')}</strong>
                  <small>{getTenantName(tenant)}</small>
                </UserText>
              </UserBlock>

              {userMenuOpen && (
                <UserDropdown>
                  <UserSummary>
                    <AccountCircleIcon color="primary" />
                    <div>
                      <strong>{getDisplayName(user, 'Usuario')}</strong>
                      <span>{getTenantName(tenant)}</span>
                    </div>
                  </UserSummary>

                  <StyledDivider />

                  {authorizedUserMenuItems.map((item, index) =>
                    item.divider ? (
                      <StyledDivider key={`user-divider-${index}`} />
                    ) : (
                      <DropdownAction
                        key={`${item.label}-${index}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleUserMenuAction(item)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleUserMenuAction(item);
                          }
                        }}
                      >
                        {item.icon}
                        {item.label}
                      </DropdownAction>
                    )
                  )}
                </UserDropdown>
              )}
            </FloatingRoot>
          </ClickAwayListener>
        </TopToolbar>

        <BreadcrumbBar aria-label="breadcrumb">
          <MuiLink
            component={RouterLink}
            to={homeDestination}
            underline="hover"
            color="inherit"
            variant="body2"
          >
            Inicio
          </MuiLink>

          {makeCrumbs().map(({ key, to, label, isLast, clickable }) =>
            isLast || !clickable ? (
              <Typography key={key} color="text.primary" variant="body2">
                {label}
              </Typography>
            ) : (
              <MuiLink
                key={key}
                component={RouterLink}
                to={to}
                underline="hover"
                color="inherit"
                variant="body2"
              >
                {label}
              </MuiLink>
            )
          )}
        </BreadcrumbBar>
      </StyledAppBar>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={closeMobileDrawer}
        PaperProps={{
          sx: {
            width: 340,
            maxWidth: '94vw',
            height: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <DrawerHeader>
          <DrawerBrand>
            <DrawerLogoImg src={logo2} alt="IntegraNube" />
          </DrawerBrand>

          <IconButton onClick={closeMobileDrawer} aria-label="cerrar menú">
            <CloseIcon />
          </IconButton>
        </DrawerHeader>

        <MuiDivider />

        <DrawerSearchSection>
          <DrawerSearchBox>
            <SearchIcon fontSize="small" />
            <input
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="Buscar funciones…"
              aria-label="Buscar funciones en el menú"
            />
            {searchText && (
              <button
                type="button"
                onClick={() => setSearchText('')}
                aria-label="Limpiar búsqueda"
              >
                <CloseIcon fontSize="small" />
              </button>
            )}
          </DrawerSearchBox>
        </DrawerSearchSection>

        {!normalizedQuery && (
          <MobileDrawerTabs role="tablist" aria-label="Secciones del menú">
            <MobileDrawerTab
              type="button"
              role="tab"
              aria-selected={mobileDrawerSection === 'modules'}
              $active={mobileDrawerSection === 'modules'}
              onClick={() => setMobileDrawerSection('modules')}
            >
              <AppsIcon fontSize="small" />
              <span>Módulos</span>
              <small>
                {
                  authorizedMenuItems.filter(
                    (item) => !item.divider
                  ).length
                }
              </small>
            </MobileDrawerTab>

            <MobileDrawerTab
              type="button"
              role="tab"
              aria-selected={mobileDrawerSection === 'favorites'}
              $active={mobileDrawerSection === 'favorites'}
              onClick={() => setMobileDrawerSection('favorites')}
            >
              <StarIcon fontSize="small" />
              <span>Favoritos</span>
              <small>{favoriteItems.length}</small>
            </MobileDrawerTab>
          </MobileDrawerTabs>
        )}

        <DrawerScrollable>
          {normalizedQuery ? (
            <>
              <DrawerSectionTitle>Resultados</DrawerSectionTitle>

              {searchResults.length > 0 ? (
                <List disablePadding>
                  {searchResults.map((item) => {
                    const isFavorite = favoriteSet.has(item.favoriteKey);

                    return (
                      <DrawerLeafRow key={`mobile-search-${item.favoriteKey}`}>
                        <ListItemButton
                          selected={isActivePath(item.path)}
                          sx={{ pr: 7 }}
                          onClick={() => handleNavigate(item.path)}
                        >
                          <ListItemIcon>{item.icon}</ListItemIcon>
                          <ListItemText
                            primary={item.label}
                            secondary={`${item.moduleLabel}${
                              item.groupLabel !== item.moduleLabel
                                ? ` › ${item.groupLabel}`
                                : ''
                            }`}
                          />
                        </ListItemButton>

                        <DrawerFavoriteButton
                          type="button"
                          $active={isFavorite}
                          disabled={favoritePendingSet.has(
                            item.favoriteKey
                          )}
                          aria-busy={favoritePendingSet.has(
                            item.favoriteKey
                          )}
                          onClick={(event) =>
                            toggleFavorite(
                              item.favoriteKey,
                              event
                            )
                          }
                        >
                          {isFavorite ? (
                            <StarIcon fontSize="small" />
                          ) : (
                            <StarBorderIcon fontSize="small" />
                          )}
                        </DrawerFavoriteButton>
                      </DrawerLeafRow>
                    );
                  })}
                </List>
              ) : (
                <DrawerEmpty>
                  No encontramos una función autorizada con ese nombre.
                </DrawerEmpty>
              )}
            </>
          ) : mobileDrawerSection === 'favorites' ? (
            <>
              <DrawerSectionTitle>
                <span>
                  <StarIcon fontSize="small" />
                  Tus favoritos
                </span>
                <small>{favoriteItems.length}</small>
              </DrawerSectionTitle>

              {favoriteItems.length > 0 ? (
                <List disablePadding>
                  {favoriteItems.map((item) => {
                    const isFavorite = favoriteSet.has(item.favoriteKey);

                    return (
                      <DrawerLeafRow key={`mobile-favorite-${item.favoriteKey}`}>
                        <ListItemButton
                          selected={isActivePath(item.path)}
                          sx={{ pr: 7 }}
                          onClick={() => handleNavigate(item.path)}
                        >
                          <ListItemIcon>{item.icon}</ListItemIcon>
                          <ListItemText
                            primary={item.label}
                            secondary={item.moduleLabel}
                          />
                        </ListItemButton>

                        <DrawerFavoriteButton
                          type="button"
                          $active={isFavorite}
                          disabled={favoritePendingSet.has(
                            item.favoriteKey
                          )}
                          aria-busy={favoritePendingSet.has(
                            item.favoriteKey
                          )}
                          onClick={(event) =>
                            toggleFavorite(
                              item.favoriteKey,
                              event
                            )
                          }
                        >
                          <StarIcon fontSize="small" />
                        </DrawerFavoriteButton>
                      </DrawerLeafRow>
                    );
                  })}
                </List>
              ) : (
                <DrawerEmpty>
                  Aún no tienes favoritos. Abre Módulos y usa la estrella
                  junto a cualquier función.
                </DrawerEmpty>
              )}
            </>
          ) : (
            <>
              <DrawerSectionTitle>
                <span>
                  <AppsIcon fontSize="small" />
                  Todos los módulos
                </span>
                <small>
                  {
                    authorizedMenuItems.filter(
                      (item) => !item.divider
                    ).length
                  }
                </small>
              </DrawerSectionTitle>

              {authorizedMenuItems.length > 0 ? (
                <List disablePadding>
                  {renderDrawerNodes(authorizedMenuItems)}
                </List>
              ) : (
                <DrawerEmpty>
                  No hay módulos autorizados para el perfil seleccionado.
                </DrawerEmpty>
              )}
            </>
          )}
        </DrawerScrollable>

        <DrawerLogoutFooter>
          <MuiDivider />

          <DrawerLogoutButton
            type="button"
            onClick={handleLogout}
            aria-label="Cerrar sesión"
          >
            <ExitToAppIcon fontSize="small" />
            <span>Cerrar sesión</span>
          </DrawerLogoutButton>
        </DrawerLogoutFooter>
      </Drawer>

      {isMobile && !hideMobileFab && (
        <Fab
          color="primary"
          onClick={() => {
            if (drawerOpen) {
              closeMobileDrawer();
            } else {
              openMobileDrawer();
            }
          }}
          sx={{
            position: 'fixed',
            bottom: (theme) => theme.spacing(8),
            right: (theme) => theme.spacing(2),
            zIndex: (theme) => theme.zIndex.snackbar + 1,
          }}
          aria-label="menú"
        >
          {drawerOpen ? <CloseIcon /> : <MenuIcon />}
        </Fab>
      )}
    </Fragment>
  );
};

export default Navbar;

/* ────────────────────────────── STYLES ────────────────────────────── */

const StyledAppBar = styled(AppBar)`
  position: relative;
  overflow: visible;
  background-color: #2c3e50;
`;

const TopToolbar = styled(Toolbar)`
  min-height: 64px;
  gap: 8px;
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  margin-right: 4px;
  text-decoration: none;
  flex-shrink: 0;
`;

const LogoImg = styled.img`
  height: 30px;
  width: auto;
  display: block;
`;

const FloatingRoot = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const MegaMenuRoot = styled.div`
  position: static;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const TopActionButton = styled.button`
  min-height: 38px;
  border: 1px solid
    ${({ $active }) => ($active ? 'rgba(26, 188, 156, 0.9)' : 'rgba(255, 255, 255, 0.15)')};
  border-radius: 9px;
  background: ${({ $active }) =>
    $active ? 'rgba(26, 188, 156, 0.15)' : 'rgba(255, 255, 255, 0.05)'};
  color: ${({ $active }) => ($active ? '#63e6c6' : '#ecf0f1')};
  padding: 7px 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font: inherit;
  font-size: 0.86rem;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 140ms ease,
    border-color 140ms ease,
    color 140ms ease;

  &:hover {
    color: #63e6c6;
    border-color: rgba(26, 188, 156, 0.65);
    background: rgba(26, 188, 156, 0.1);
  }
`;

const CountBadge = styled.span`
  min-width: 19px;
  height: 19px;
  padding: 0 5px;
  border-radius: 999px;
  background: #1abc9c;
  color: #17324a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 700;
`;

const ProgressiveMenuPanel = styled.div`
  --column-width: 280px;

  position: absolute;
  top: 64px;
  left: 12px;
  width: min(
    calc(var(--mega-column-count) * var(--column-width)),
    calc(100vw - 24px)
  );
  min-width: min(var(--column-width), calc(100vw - 24px));
  max-width: calc(100vw - 24px);
  background: #fff;
  color: #243447;
  border: 1px solid #dbe3e9;
  border-radius: 0 0 12px 12px;
  box-shadow: 0 18px 42px rgba(15, 35, 50, 0.25);
  overflow: hidden;
  z-index: 1500;
`;




const ProgressiveColumns = styled.div`
  display: flex;
  align-items: stretch;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  scrollbar-width: thin;
`;

const ProgressiveColumn = styled.section`
  width: var(--column-width);
  min-width: var(--column-width);
  max-width: var(--column-width);
  min-height: 360px;
  max-height: calc(100vh - 82px);
  border-right: 1px solid #e5eaee;
  background: #fff;
  display: flex;
  flex-direction: column;

  &:last-child {
    border-right: 0;
  }

  &:first-child {
    background: #f8fafb;
  }
`;

const ProgressiveColumnHeader = styled.div`
  min-height: 58px;
  padding: 11px 13px 9px;
  border-bottom: 1px solid #edf1f4;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;

  strong {
    color: #273e53;
    font-size: 0.88rem;
    text-transform: uppercase;
    letter-spacing: 0.025em;
  }

  span {
    color: #8794a0;
    font-size: 0.7rem;
  }
`;

const ProgressiveItems = styled.div`
  min-height: 0;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${({ $firstColumn }) => ($firstColumn ? '5px 0 0' : '6px 0')};
  scrollbar-width: thin;
  scrollbar-gutter: stable;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #a9b3bc;
    border-radius: 999px;
    border: 2px solid #f8fafb;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #8996a1;
  }
`;

const ProgressiveItemRow = styled.div`
  position: relative;
  min-height: 45px;
  display: flex;
  align-items: stretch;
  border-left: 3px solid
    ${({ $selected, $active }) =>
      $selected || $active ? '#1abc9c' : 'transparent'};
  background: ${({ $selected, $active }) =>
    $selected || $active ? 'rgba(26, 188, 156, 0.1)' : 'transparent'};

  transition:
    background 100ms ease,
    border-color 100ms ease;

  &:hover {
    background: ${({ $selected, $active }) =>
      $selected || $active
        ? 'rgba(26, 188, 156, 0.13)'
        : '#f1f5f7'};
  }
`;

const ProgressiveItemButton = styled.button`
  min-width: 0;
  flex: 1;
  min-height: 45px;
  border: 0;
  background: transparent;
  color: inherit;
  padding: 7px 9px 7px 10px;
  display: flex;
  align-items: center;
  gap: 9px;
  text-align: left;
  font: inherit;
  cursor: pointer;
`;

const ProgressiveItemIcon = styled.span`
  width: 26px;
  height: 26px;
  flex: 0 0 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 1.08rem;
  }
`;

const ProgressiveItemText = styled.span`
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;

  strong {
    overflow: hidden;
    color: #34495e;
    font-size: 0.8rem;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    color: #8996a2;
    font-size: 0.66rem;
  }
`;

const ProgressiveArrow = styled.span`
  width: 22px;
  height: 22px;
  flex: 0 0 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #84929f;
`;

const ProgressiveFavoriteWrap = styled.span`
  width: 39px;
  flex: 0 0 39px;
  display: flex;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transition: opacity 120ms ease;

  ${ProgressiveItemRow}:hover & {
    opacity: 1;
  }

  button {
    width: 39px;
    flex-basis: 39px;
  }
`;

const ProgressiveDivider = styled.div`
  height: 1px;
  background: #e7ecef;
  margin: 6px 12px;
`;

const FunctionRow = styled.div`
  min-height: 48px;
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid #f0f3f5;
  background: ${({ $active }) => ($active ? 'rgba(26, 188, 156, 0.08)' : '#fff')};

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: #f4f8fa;
  }
`;

const FunctionLinkButton = styled.button`
  min-width: 0;
  flex: 1;
  border: 0;
  background: transparent;
  color: inherit;
  padding: 8px 8px 8px 12px;
  display: flex;
  align-items: center;
  gap: 9px;
  text-align: left;
  font: inherit;
  cursor: pointer;
`;

const FunctionIcon = styled.span`
  width: 25px;
  height: 25px;
  flex: 0 0 25px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 1.08rem;
  }
`;

const FunctionText = styled.span`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;

  strong {
    overflow: hidden;
    color: #34495e;
    font-size: 0.8rem;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    overflow: hidden;
    color: #8896a3;
    font-size: 0.68rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const FavoriteButton = styled.button`
  width: ${({ $compact }) => ($compact ? '36px' : '42px')};
  flex: 0 0 ${({ $compact }) => ($compact ? '36px' : '42px')};
  border: 0;
  border-left: 1px solid #f0f3f5;
  background: transparent;
  color: ${({ $active }) => ($active ? '#f5b301' : '#a5afb8')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover:not(:disabled) {
    color: #f5b301;
    background: rgba(245, 179, 1, 0.08);
  }

  &:disabled {
    cursor: wait;
    opacity: 0.58;
  }
`;


const FavoritesPanel = styled.div`
  position: absolute;
  top: calc(100% + 12px);
  left: 0;
  width: 390px;
  max-height: 520px;
  overflow-y: auto;
  background: #fff;
  color: #243447;
  border: 1px solid #dfe6ec;
  border-radius: 12px;
  box-shadow: 0 16px 38px rgba(15, 35, 50, 0.22);
  padding: 8px;
  z-index: 1600;
`;

const PanelHeader = styled.div`
  padding: 8px 9px 11px;
  border-bottom: 1px solid #edf1f4;

  > div {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  strong {
    color: #263d52;
    font-size: 0.92rem;
  }

  span {
    color: #81909e;
    font-size: 0.74rem;
  }
`;

const PanelList = styled.div`
  padding-top: 5px;
`;

const SearchArea = styled.div`
  position: relative;
  min-width: 220px;
  max-width: 720px;
  flex: 1 1 480px;
`;

const SearchBox = styled.div`
  height: 40px;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid
    ${({ $open }) => ($open ? 'rgba(26, 188, 156, 0.95)' : 'rgba(255, 255, 255, 0.17)')};
  border-radius: 10px;
  background: ${({ $open }) =>
    $open ? 'rgba(255, 255, 255, 0.13)' : 'rgba(255, 255, 255, 0.08)'};
  color: #dfe8ef;
  padding: 0 9px 0 11px;
  transition:
    border-color 140ms ease,
    background 140ms ease;

  &:focus-within {
    border-color: rgba(26, 188, 156, 0.95);
    background: rgba(255, 255, 255, 0.13);
  }
`;

const SearchInput = styled.input`
  min-width: 0;
  flex: 1;
  border: 0;
  outline: 0;
  background: transparent;
  color: #fff;
  font: inherit;
  font-size: 0.86rem;

  &::placeholder {
    color: rgba(236, 240, 241, 0.72);
  }
`;

const ShortcutHint = styled.span`
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-bottom-width: 2px;
  border-radius: 5px;
  padding: 2px 5px;
  color: rgba(236, 240, 241, 0.72);
  font-size: 0.66rem;
`;

const ClearSearchButton = styled.button`
  width: 27px;
  height: 27px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #dfe8ef;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const SearchPanel = styled.div`
  position: absolute;
  top: calc(100% + 12px);
  left: 0;
  right: 0;
  min-width: 480px;
  max-height: 540px;
  overflow-y: auto;
  background: #fff;
  color: #243447;
  border: 1px solid #dfe6ec;
  border-radius: 12px;
  box-shadow: 0 16px 38px rgba(15, 35, 50, 0.22);
  padding: 8px;
  z-index: 1700;
`;

const SearchPanelTitle = styled.div`
  padding: 8px 10px 9px;
  border-bottom: 1px solid #edf1f4;
  color: #718190;
  font-size: 0.76rem;
  font-weight: 600;
`;

const EmptyState = styled.div`
  padding: 24px 18px;
  color: #778694;
  font-size: 0.82rem;
  line-height: 1.45;
  text-align: center;
`;

const DesktopConnection = styled.div`
  min-width: 76px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MobileConnection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Spacer = styled.div`
  flex-grow: 1;
`;

const UserBlock = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #ecf0f1;
  cursor: pointer;
  user-select: none;

  &:hover {
    color: #63e6c6;
  }

  @media (max-width: 960px) {
    gap: 0;
  }
`;

const UserText = styled.div`
  max-width: 190px;
  display: flex;
  flex-direction: column;
  color: inherit;
  font-size: 0.82rem;
  line-height: 1.12;

  strong,
  small {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    opacity: 0.8;
  }

  @media (max-width: 960px) {
    display: none;
  }
`;

const UserDropdown = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 12px);
  width: 270px;
  max-height: 560px;
  overflow-y: auto;
  background: #fff;
  color: #243447;
  border: 1px solid #dfe6ec;
  border-radius: 12px;
  box-shadow: 0 16px 38px rgba(15, 35, 50, 0.22);
  padding: 7px 0;
  z-index: 1800;
`;

const UserSummary = styled.div`
  padding: 9px 14px;
  display: flex;
  align-items: center;
  gap: 10px;

  > div {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  strong,
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    color: #2c3e50;
    font-size: 0.86rem;
  }

  span {
    color: #80909e;
    font-size: 0.72rem;
  }
`;

const DropdownAction = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  color: #2c3e50;
  font-size: 0.88rem;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: #ecf0f1;
  }
`;

const StyledDivider = styled.div`
  height: 1px;
  background-color: #e5e7eb;
  margin: 6px 12px;
`;

const BreadcrumbBar = styled(Breadcrumbs)`
  background: #f5f5f5;
  padding: 8px 16px;
`;

const DrawerHeader = styled.div`
  flex: 0 0 auto;
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px 0 16px;
`;

const DrawerBrand = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
`;

const DrawerLogoImg = styled.img`
  display: block;
  width: auto;
  height: 42px;
  max-width: 238px;
  object-fit: contain;
  object-position: left center;

  @media (max-width: 380px) {
    height: 38px;
    max-width: 210px;
  }
`;

const DrawerSearchSection = styled.div`
  flex: 0 0 auto;
  padding: 12px;
`;

const DrawerSearchBox = styled.div`
  height: 42px;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #d7e0e7;
  border-radius: 10px;
  background: #f7f9fb;
  color: #607284;
  padding: 0 8px 0 11px;

  &:focus-within {
    border-color: #1abc9c;
    box-shadow: 0 0 0 2px rgba(26, 188, 156, 0.1);
  }

  input {
    min-width: 0;
    flex: 1;
    border: 0;
    outline: 0;
    background: transparent;
    color: #2c3e50;
    font: inherit;
    font-size: 0.86rem;
  }

  button {
    width: 28px;
    height: 28px;
    border: 0;
    border-radius: 7px;
    background: transparent;
    color: #607284;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover {
      background: #e9eef2;
    }
  }
`;

const MobileDrawerTabs = styled.div`
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  padding: 0 12px 10px;
  border-bottom: 1px solid #e5eaee;
`;

const MobileDrawerTab = styled.button`
  min-width: 0;
  min-height: 42px;
  border: 1px solid
    ${({ $active }) => ($active ? '#1abc9c' : '#d8e0e6')};
  border-radius: 10px;
  background: ${({ $active }) =>
    $active ? 'rgba(26, 188, 156, 0.11)' : '#ffffff'};
  color: ${({ $active }) => ($active ? '#137d69' : '#536677')};
  padding: 7px 9px;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 7px;
  cursor: pointer;
  font: inherit;
  font-size: 0.79rem;
  font-weight: 700;
  text-align: left;

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    min-width: 22px;
    height: 22px;
    padding: 0 5px;
    border-radius: 999px;
    background: ${({ $active }) =>
      $active ? 'rgba(26, 188, 156, 0.18)' : '#edf2f5'};
    color: inherit;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.68rem;
  }

  &:active {
    transform: translateY(1px);
  }
`;

const DrawerScrollable = styled.div`
  min-height: 0;
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
  padding-bottom: 8px;
`;

const DrawerLogoutFooter = styled.div`
  flex: 0 0 auto;
  background: #ffffff;
  box-shadow: 0 -5px 14px rgba(44, 62, 80, 0.06);
  padding-bottom: env(safe-area-inset-bottom, 0px);
`;

const DrawerLogoutButton = styled.button`
  width: 100%;
  min-height: 52px;
  border: 0;
  background: #ffffff;
  color: #c62828;
  padding: 0 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  font: inherit;
  font-size: 0.92rem;
  font-weight: 700;
  text-align: left;

  &:hover {
    background: rgba(198, 40, 40, 0.06);
  }

  &:active {
    background: rgba(198, 40, 40, 0.11);
  }

  svg {
    flex: 0 0 auto;
  }
`;

const DrawerSectionTitle = styled.div`
  min-height: 38px;
  padding: 8px 14px 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #687a8c;
  font-size: 0.73rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;

  > span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  small {
    min-width: 20px;
    height: 20px;
    border-radius: 999px;
    background: #edf2f5;
    color: #607284;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.68rem;
  }
`;

const DrawerLeafRow = styled.div`
  position: relative;

  .MuiListItemButton-root {
    min-height: 48px;
  }

  .MuiListItemIcon-root {
    min-width: 40px;
  }
`;

const DrawerFavoriteButton = styled.button`
  position: absolute;
  top: 50%;
  right: 9px;
  width: 35px;
  height: 35px;
  transform: translateY(-50%);
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: ${({ $active }) => ($active ? '#f5b301' : '#a3adb7')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover:not(:disabled) {
    color: #f5b301;
    background: rgba(245, 179, 1, 0.1);
  }

  &:disabled {
    cursor: wait;
    opacity: 0.58;
  }
`;

const DrawerEmpty = styled.div`
  margin: 4px 12px 12px;
  padding: 14px;
  border: 1px dashed #d8e0e6;
  border-radius: 10px;
  background: #fafcfd;
  color: #758595;
  font-size: 0.78rem;
  line-height: 1.4;
`;
